<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="table-heading"
      class="mb-4"
    />
    <v-skeleton-loader type="image" />
  </div>

  <div v-else>
    <div class="floor-header">
      <span class="floor-header__title text--primary">フロア</span>
      <v-btn
        small
        :outlined="!compactMode"
        :color="compactMode ? 'primary' : 'grey'"
        class="mr-2"
        @click="compactMode = !compactMode"
      >
        <v-icon
          size="16"
          class="mr-1"
        >
          mdi-view-compact
        </v-icon>
        コンパクト
      </v-btn>
      <v-btn
        small
        outlined
        color="grey"
        class="floor-header__wide-btn"
        @click="toggleWideMode"
      >
        <v-icon
          size="16"
          class="mr-1"
        >
          {{ wideMode ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}
        </v-icon>
        {{ wideMode ? '元に戻す' : '拡大表示' }}
      </v-btn>
      <div
        v-if="!compactMode"
        class="floor-header__zoom"
      >
        <v-btn
          small
          outlined
          color="grey"
          @click="zoomOut"
        >
          ー
        </v-btn>
        <span class="floor-header__zoom-label text--primary">{{ Math.round(zoomLevel * 100) }}%</span>
        <v-btn
          small
          outlined
          color="grey"
          @click="zoomIn"
        >
          ＋
        </v-btn>
        <v-btn
          small
          outlined
          color="grey"
          class="ml-2"
          @click="fitToContent"
        >
          全体表示
        </v-btn>
      </div>
    </div>

    <div
      v-if="halls.length > 0 && isLayoutConfigured"
      class="floor-main"
    >
      <!-- コンパクトモード -->
      <div
        v-if="compactMode"
        class="floor-content"
      >
        <div class="compact-view">
          <div
            v-for="(group, gIdx) in compactGroups"
            :key="`cg-${gIdx}`"
            class="compact-zone"
            :style="{ borderColor: group.color }"
          >
            <div
              class="compact-zone__label"
              :style="{ color: group.color }"
            >
              {{ group.name }}
            </div>
            <div class="compact-zone__grid">
              <div
                v-for="(layout, layoutIdx) in group.layouts"
                :key="`ct-${layout._hall ? layout._hall.id : 'x'}-${layout.table_number}`"
                class="compact-card"
                :class="[getTableStatusClass(layout), { 'compact-card--child': !!layout._childGroup }]"
                :style="compactCardStyle(layout)"
                @click="onTableClick(group.layoutStartIdx + layoutIdx)"
                @dragover="onTableDragOver($event, layout)"
                @dragleave="onTableDragLeave($event)"
                @drop="onTableDrop($event, layout)"
              >
                <div class="compact-card__name">
                  {{ layout.name || `${layout._hall.attributes.tablePrefix}${layout.table_number}${layout._hall.attributes.tableSuffix}` }}
                  <!-- 予約由来の卓（紫=予約の意味づけ。枠線色の時間ステータスとは別軸） -->
                  <span
                    v-if="layout._table && layout._table.attributes.fromReservation"
                    class="table-rsv-pill"
                  >予約</span>
                  <!-- 卓の連結(親卓): 「+N」チップ。タップで編集 -->
                  <button
                    v-if="layout._group"
                    type="button"
                    class="compact-card__group-chip"
                    :style="{ backgroundColor: layout._groupColor }"
                    title="連結を編集"
                    @click.stop="openTableGroupFromFloor(layout)"
                    @mousedown.stop
                    @touchstart.stop
                  >
                    +{{ layout._group.attributes.tableNumbers.length }}
                  </button>
                </div>
                <template v-if="layout._table && layout._table.hold === undefined">
                  <!-- セット中: 残り時間を主表示（超過は赤のマイナス表示） / 無制限: 開始時刻 + 経過時間 -->
                  <div
                    v-if="layout._table.meta.expireAt"
                    class="compact-card__time"
                    :class="`compact-card__time--${getRemainingTimeStatus(layout._table.meta.expireAt)}`"
                  >
                    <span
                      v-if="isOvertime(layout._table.meta.expireAt)"
                      class="compact-card__overtime-label"
                    >超過</span>
                    {{ formatRemainingSigned(layout._table.meta.expireAt) }}
                  </div>
                  <div
                    v-else
                    class="compact-card__time"
                  >
                    <span class="compact-card__start">{{ formatTime(layout._table.attributes.startAt) }}〜</span>
                    {{ formatElapsed(layout._table.attributes.startAt) }}
                  </div>
                  <div
                    v-if="layout._table.meta.expireAt"
                    class="compact-card__range"
                  >
                    {{ formatTimeRange(layout._table.attributes.startAt, layout._table.meta.expireAt) }}
                  </div>
                  <div class="compact-card__meta">
                    <span class="compact-card__count">
                      {{ layout._table.attributes.customerCount }}名
                    </span>
                    <v-icon
                      v-if="hasCautionCustomer(layout._table)"
                      x-small
                      class="floor-caution-icon"
                    >
                      {{ icons.mdiAlert }}
                    </v-icon>
                    <auto-extension-badge
                      :enabled="layout._table.attributes.autoExtensionEnabled"
                      compact
                    />
                    <!-- 卓の連結: 未連結の稼働中卓から連結を始める -->
                    <button
                      v-if="!layout._group"
                      type="button"
                      class="compact-card__group-btn"
                      title="卓を連結"
                      @click.stop="openTableGroupFromFloor(layout)"
                      @mousedown.stop
                      @touchstart.stop
                    >
                      <v-icon size="11">
                        {{ icons.mdiLinkVariant }}
                      </v-icon>
                    </button>
                  </div>
                  <div class="compact-card__total">
                    ¥{{ (layout._table.meta.total || 0).toLocaleString() }}
                    <span
                      v-if="layout._table.meta.tableCharge && layout._table.meta.tableCharge.applied"
                      class="table-charge-badge"
                    >
                      {{ layout._table.meta.tableCharge.name }}
                    </span>
                  </div>
                  <div
                    v-if="getTableCasts(layout._table).length > 0"
                    class="compact-card__casts"
                    role="button"
                    tabindex="0"
                    title="クリックで解除"
                    @click.stop="openRemoveForTable(layout._table)"
                    @mousedown.stop
                    @touchstart.stop
                  >
                    <span
                      v-for="cast in getCompactCastsSplit(layout._table).visible"
                      :key="`compact-${cast.type}-${cast.userId}`"
                      class="compact-card__cast"
                      :style="{ borderLeftColor: getCastColor(cast) }"
                    >
                      {{ cast.name }}
                    </span>
                    <span
                      v-if="getCompactCastsSplit(layout._table).overflowCount > 0"
                      class="compact-card__cast-overflow"
                    >
                      +{{ getCompactCastsSplit(layout._table).overflowCount }}名
                    </span>
                  </div>
                </template>
                <template v-else-if="layout._table && layout._table.hold">
                  <div class="compact-card__hold">
                    ...
                  </div>
                </template>
                <template v-else-if="layout._childGroup">
                  <!-- 子卓(連結中の空卓): 入店受付は開かず、タップで解除確認 -->
                  <div
                    class="compact-card__empty compact-card__child"
                    :style="{ color: layout._groupColor }"
                  >
                    <v-icon
                      size="12"
                      :color="layout._groupColor"
                    >
                      {{ icons.mdiLinkVariant }}
                    </v-icon>
                    {{ childGroupParentName(layout) }}と連結
                  </div>
                </template>
                <template v-else>
                  <div class="compact-card__empty">
                    空席
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- マップモード -->
      <div
        v-if="!compactMode"
        class="floor-content"
      >
        <div
          class="floor-canvas-wrapper"
          :class="{ 'floor-canvas-wrapper--wide': wideMode }"
        >
          <div
            class="floor-canvas"
            :style="{ ...canvasStyle, transform: `scale(${zoomLevel})`, transformOrigin: 'top left' }"
          >
            <!-- エリア（ゾーン）背景 -->
            <div
              v-for="zone in floorZones"
              :key="`zone-${zone.hallId}`"
              class="floor-zone"
              :style="zoneStyle(zone)"
            >
              <span class="floor-zone__label">{{ zone.name }}</span>
            </div>
            <template v-for="(layout, layoutIdx) in currentLayouts">
              <div
                :key="`floor_table_${layout._hall ? layout._hall.id : 'x'}_${layout.table_number}`"
                class="floor-table"
                :class="[
                  getTableStatusClass(layout),
                  {
                    'floor-table--drop-target': dragCastId && layout._table && !layout._table.hold,
                    'floor-table--narrow': isNarrowLayout(layout),
                    'floor-table--child': !!layout._childGroup,
                  },
                ]"
                :style="tableStyle(layout)"
                @click="onTableClick(layoutIdx)"
                @dragover="onTableDragOver($event, layout)"
                @dragleave="onTableDragLeave($event)"
                @drop="onTableDrop($event, layout)"
              >
                <!-- 使用中 -->
                <template v-if="layout._table && layout._table.hold === undefined">
                  <div class="floor-table__header">
                    <span class="floor-table__name">
                      {{ layout.name || `${layout._hall.attributes.tablePrefix}${layout.table_number}${layout._hall.attributes.tableSuffix}` }}
                    </span>
                    <span
                      v-if="layout._table.attributes.fromReservation"
                      class="table-rsv-pill"
                    >予約</span>
                    <span class="floor-table__guest-badge">
                      {{ layout._table.attributes.customerCount }}名
                    </span>
                    <v-icon
                      v-if="hasCautionCustomer(layout._table)"
                      x-small
                      class="floor-caution-icon"
                    >
                      {{ icons.mdiAlert }}
                    </v-icon>
                    <auto-extension-badge
                      :enabled="layout._table.attributes.autoExtensionEnabled"
                      compact
                    />
                    <!-- 卓の連結: 親卓は「+N」チップ(タップで編集)、未連結は小さな連結ボタン -->
                    <button
                      v-if="layout._group"
                      type="button"
                      class="floor-table__group-chip"
                      :style="{ backgroundColor: layout._groupColor }"
                      title="連結を編集"
                      @click.stop="openTableGroupFromFloor(layout)"
                      @mousedown.stop
                      @touchstart.stop
                    >
                      <v-icon
                        size="11"
                        color="#fff"
                      >
                        {{ icons.mdiLinkVariant }}
                      </v-icon>
                      +{{ layout._group.attributes.tableNumbers.length }}
                    </button>
                    <button
                      v-else
                      type="button"
                      class="floor-table__group-btn"
                      title="卓を連結"
                      @click.stop="openTableGroupFromFloor(layout)"
                      @mousedown.stop
                      @touchstart.stop
                    >
                      <v-icon size="13">
                        {{ icons.mdiLinkVariant }}
                      </v-icon>
                    </button>
                  </div>
                  <!-- セット中: 残り時間を主表示（超過は赤のマイナス表示）+ 開始〜終了予定 -->
                  <template v-if="layout._table.meta.expireAt">
                    <div
                      class="floor-table__time"
                      :class="`floor-table__time--${getRemainingTimeStatus(layout._table.meta.expireAt)}`"
                    >
                      <span
                        v-if="isOvertime(layout._table.meta.expireAt)"
                        class="floor-table__overtime-label"
                      >超過</span>
                      {{ formatRemainingSigned(layout._table.meta.expireAt) }}
                    </div>
                  </template>
                  <!-- セット時間なし（無制限）: 従来どおり開始時刻 + 経過時間 -->
                  <div
                    v-else
                    class="floor-table__time"
                  >
                    <span class="floor-table__start">{{ formatTime(layout._table.attributes.startAt) }}〜</span>
                    {{ formatElapsed(layout._table.attributes.startAt) }}
                  </div>
                  <!-- 開始〜終了予定と合計金額。幅があれば1行、狭ければ折り返す（固定寸法の卓カードで行数を増やさない） -->
                  <div
                    v-if="!compactMode"
                    class="floor-table__info"
                  >
                    <span
                      v-if="layout._table.meta.expireAt"
                      class="floor-table__sub"
                    >
                      {{ formatTimeRange(layout._table.attributes.startAt, layout._table.meta.expireAt) }}
                    </span>
                    <span class="floor-table__total">
                      ¥{{ (layout._table.meta.total || 0).toLocaleString() }}
                      <span
                        v-if="layout._table.meta.tableCharge && layout._table.meta.tableCharge.applied"
                        class="table-charge-badge"
                      >
                        {{ layout._table.meta.tableCharge.name }}
                      </span>
                    </span>
                  </div>
                  <div
                    v-if="!compactMode && getTableCasts(layout._table).length > 0"
                    class="floor-table__casts"
                  >
                    <span
                      v-for="cast in getTableCastsSplit(layout._table).visible"
                      :key="`${cast.type}-${cast.userId}`"
                      class="floor-table__cast-chip"
                      :style="{ borderLeft: `3px solid ${getCastColor(cast)}` }"
                    >
                      <span class="floor-table__cast-type">{{ cast.type === 'nomination' ? cast.vendibleName : 'フリー' }}</span>
                      {{ cast.name }}
                      <button
                        v-if="getTableCastsSplit(layout._table).overflowCount === 0"
                        class="floor-table__cast-remove"
                        @click.stop="removeCast(layout._table, cast)"
                      >
                        ×
                      </button>
                    </span>
                    <button
                      v-if="getTableCastsSplit(layout._table).overflowCount > 0"
                      class="floor-table__cast-overflow"
                      @click.stop="openRemoveForTable(layout._table)"
                      @mousedown.stop
                      @touchstart.stop
                    >
                      +{{ getTableCastsSplit(layout._table).overflowCount }}名
                    </button>
                  </div>
                  <div
                    v-if="!compactMode && layout._table.attributes.customerTableTaggings && layout._table.attributes.customerTableTaggings.data.length > 0"
                    class="floor-table__tags"
                  >
                    <span
                      v-for="tag in layout._table.attributes.customerTableTaggings.data.slice(0, 2)"
                      :key="tag.id"
                      class="floor-table__tag"
                    >
                      {{ tag.attributes.customerTagName }}
                    </span>
                  </div>
                </template>

                <!-- 他のウェイターが使用中 -->
                <template v-else-if="layout._table && layout._table.hold">
                  <div class="floor-table__empty floor-table__hold">
                    <v-progress-circular
                      indeterminate
                      size="20"
                      width="2"
                    />
                  </div>
                </template>

                <!-- 空席 -->
                <template v-else>
                  <div class="floor-table__empty">
                    <div class="floor-table__empty-name">
                      {{ layout.name || `${layout._hall.attributes.tablePrefix}${layout.table_number}${layout._hall.attributes.tableSuffix}` }}
                    </div>
                    <template v-if="layout._childGroup">
                      <v-icon
                        size="22"
                        :color="layout._groupColor"
                      >
                        {{ icons.mdiLinkVariant }}
                      </v-icon>
                      <span class="floor-table__child-label">
                        {{ childGroupParentName(layout) }}と連結
                      </span>
                    </template>
                    <v-icon
                      v-else
                      size="24"
                      color="grey"
                    >
                      mdi-plus
                    </v-icon>
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 右サイドパネル（両モード共通）。縦積み5セクションからタブ切替に再編（2026-08-03） -->
      <div class="side-panel">
        <!-- タブ（sticky・件数バッジ付き） -->
        <div class="side-tabs">
          <button
            type="button"
            class="side-tab"
            :class="{ 'side-tab--on': sideTab === 'casts' }"
            @click="sideTab = 'casts'"
          >
            キャスト
            <span class="side-tab__count">{{ workingCasts.length }}</span>
            <span
              v-if="lateCount > 0"
              class="side-tab__alert-dot"
              :title="`未出勤のキャストが${lateCount}名います`"
            ></span>
          </button>
          <button
            type="button"
            class="side-tab"
            :class="{ 'side-tab--on': sideTab === 'reservations' }"
            @click="sideTab = 'reservations'"
          >
            予約
            <span class="side-tab__count">{{ todayReservations.length }}</span>
          </button>
          <button
            type="button"
            class="side-tab"
            :class="{ 'side-tab--on': sideTab === 'tables' }"
            @click="sideTab = 'tables'"
          >
            卓時間
            <span class="side-tab__count">{{ tableTimeSummary.length }}</span>
          </button>
        </div>

        <!-- キャストタブ -->
        <template v-if="sideTab === 'casts'">
          <div class="cast-filter-bar">
            <button
              v-for="filter in castFilterOptions"
              :key="`cf-${filter.value}`"
              type="button"
              class="cast-fchip"
              :class="{ 'cast-fchip--on': castFilter === filter.value }"
              @click="castFilter = filter.value"
            >
              {{ filter.label }} <span class="cast-fchip__n">{{ filter.count }}</span>
            </button>
            <button
              type="button"
              class="cast-fchip"
              :class="{ 'cast-fchip--on': showAllRoles }"
              :title="showAllRoles ? '他スタッフ表示中（クリックで非表示に）' : '他スタッフ非表示中（クリックで表示）'"
              @click="showAllRoles = !showAllRoles"
            >
              他スタッフ{{ showAllRoles ? ' ✓' : '' }}
            </button>
          </div>

          <div
            v-if="castList.length === 0"
            class="side-panel__section side-panel__empty"
          >
            出勤中のキャストはいません
          </div>
          <div
            v-if="castList.length > 0"
            class="side-panel__section"
          >
            <!-- 配置中 -->
            <div
              v-if="(castFilter === 'all' || castFilter === 'assigned') && assignedCasts.length > 0"
              class="cast-panel__group"
            >
              <div class="cast-panel__group-label cast-panel__group-label--assigned">
                配置中 ({{ assignedCasts.length }})
              </div>
              <div class="cast-panel__list">
                <div
                  v-for="cast in assignedCasts"
                  :key="cast.id"
                  class="cast-card cast-card--assigned"
                  :class="{ 'cast-card--dragging': dragCastId === cast.id }"
                  draggable="true"
                  @dragstart="onCastDragStart($event, cast)"
                  @dragend="onCastDragEnd"
                >
                  <div class="cast-card__top">
                    <span class="cast-card__name">{{ cast.name }}</span>
                    <span
                      v-if="cast._shiftTime"
                      class="cast-card__shift-end"
                    >
                      {{ cast._shiftTime }}
                    </span>
                    <v-btn
                      v-if="timecardStatuses.length > 0"
                      icon
                      x-small
                      class="cast-card__status-btn"
                      title="勤怠ステータスを設定"
                      @click.stop="openStatusDialogForCast(cast)"
                    >
                      <v-icon size="14">
                        mdi-account-clock
                      </v-icon>
                    </v-btn>
                  </div>
                  <div
                    v-for="(at, atIdx) in cast._assignedTables"
                    :key="atIdx"
                    class="cast-card__assignment"
                  >
                    <span
                      class="cast-card__table-badge cast-card__table-badge--clickable"
                      :style="{ background: getCastColor(at) }"
                      role="button"
                      tabindex="0"
                      title="クリックで解除"
                      @click.stop="openRemoveFromCastCard(cast.id, at)"
                    >
                      {{ at.name }}
                    </span>
                    <span
                      v-if="at.since"
                      class="cast-card__duration"
                    >
                      {{ formatElapsed(at.since) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- フリー（2列グリッド: 人数が多いためスクロールを圧縮） -->
            <div
              v-if="(castFilter === 'all' || castFilter === 'free') && freeCasts.length > 0"
              class="cast-panel__group"
            >
              <div class="cast-panel__group-label cast-panel__group-label--free">
                待機 ({{ freeCasts.length }})
              </div>
              <div class="free-grid">
                <div
                  v-for="cast in freeCasts"
                  :key="cast.id"
                  class="free-chip"
                  :class="{ 'cast-card--dragging': dragCastId === cast.id }"
                  draggable="true"
                  :title="cast._shiftTime ? `${cast.name} ${cast._shiftTime}` : cast.name"
                  @dragstart="onCastDragStart($event, cast)"
                  @dragend="onCastDragEnd"
                >
                  <span class="free-chip__name text-truncate">{{ cast.name }}</span>
                  <v-btn
                    v-if="timecardStatuses.length > 0"
                    icon
                    x-small
                    class="free-chip__status-btn"
                    title="勤怠ステータスを設定"
                    @click.stop="openStatusDialogForCast(cast)"
                  >
                    <v-icon size="14">
                      mdi-account-clock
                    </v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- 勤怠ステータス（欠勤・遅刻など）：ステータス名ごとにグルーピング -->
            <template v-if="castFilter === 'all'">
              <div
                v-for="group in statusGroups"
                :key="group.name"
                class="cast-panel__group"
              >
                <div class="cast-panel__group-label cast-panel__group-label--status">
                  {{ group.name }} ({{ group.casts.length }})
                </div>
                <div class="cast-panel__list">
                  <div
                    v-for="cast in group.casts"
                    :key="cast.id"
                    class="cast-card cast-card--status"
                  >
                    <div class="cast-card__top">
                      <span class="cast-card__name">{{ cast.name }}</span>
                      <span
                        v-if="cast._shiftTime"
                        class="cast-card__shift-end"
                      >
                        {{ cast._shiftTime }}
                      </span>
                      <v-btn
                        icon
                        x-small
                        class="cast-card__status-btn"
                        title="勤怠ステータスを変更"
                        @click.stop="openStatusDialogForCast(cast)"
                      >
                        <v-icon size="14">
                          mdi-account-clock
                        </v-icon>
                      </v-btn>
                    </div>
                    <div class="cast-card__status-row">
                      <button
                        type="button"
                        class="cast-card__status cast-card__status--tappable"
                        title="タップして勤怠ステータスを変更"
                        @click.stop="openStatusDialogForCast(cast)"
                      >
                        {{ group.name }}
                      </button>
                      <v-btn
                        x-small
                        outlined
                        color="primary"
                        @click.stop="openCheckInTimeDialog({
                          userId: cast.id, name: cast.name, timecardId: cast._timecardId,
                        })"
                      >
                        出勤にする
                      </v-btn>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <!-- 出勤スタッフ（キャスト以外、トグル ON 時のみ表示） -->
          <div
            v-if="castFilter === 'all' && showAllRoles && staffList.length > 0"
            class="side-panel__section"
          >
            <div class="side-panel__section-header">
              <span class="side-panel__section-title">出勤スタッフ</span>
              <span class="side-panel__section-count">{{ staffList.length }}名</span>
            </div>
            <div class="cast-panel__list">
              <div
                v-for="staff in staffList"
                :key="`staff-${staff.id}`"
                class="cast-card cast-card--staff"
              >
                <div class="cast-card__top">
                  <span class="cast-card__name">{{ staff.name }}</span>
                  <span
                    v-if="staff._shiftTime"
                    class="cast-card__shift-end"
                  >
                    {{ staff._shiftTime }}
                  </span>
                </div>
                <div class="cast-card__role">
                  {{ staff.role }}
                </div>
              </div>
            </div>
          </div>

          <!-- これから出勤 -->
          <div
            v-if="(castFilter === 'all' || castFilter === 'upcoming') && upcomingCasts.length > 0"
            class="side-panel__section"
          >
            <div class="side-panel__section-header">
              <span class="side-panel__section-title">出勤予定</span>
            </div>
            <div class="cast-panel__list">
              <div
                v-for="cast in upcomingCasts"
                :key="`upcoming-${cast.userId}`"
                class="cast-card cast-card--upcoming"
                :class="{ 'cast-card--late': cast.isLate }"
              >
                <div class="cast-card__top">
                  <span class="cast-card__name">{{ cast.name }}</span>
                  <span
                    v-if="cast.isLate"
                    class="cast-card__late-badge"
                  >
                    未出勤
                  </span>
                </div>
                <div class="cast-card__schedule">
                  <template v-if="cast.startAt">
                    {{ cast.startAt }} 〜 {{ cast.endAt || '未定' }}
                  </template>
                  <template v-else>
                    時間未定
                  </template>
                </div>
                <div class="cast-card__actions">
                  <v-btn
                    x-small
                    color="primary"
                    class="me-1"
                    @click="openCheckInTimeDialog({ userId: cast.userId, name: cast.name })"
                  >
                    出勤
                  </v-btn>
                  <v-btn
                    v-if="timecardStatuses.length > 0"
                    x-small
                    outlined
                    color="grey"
                    @click="openStatusDialogForUpcoming(cast)"
                  >
                    欠勤等
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 予約タブ -->
        <template v-else-if="sideTab === 'reservations'">
          <div
            v-if="todayReservations.length === 0"
            class="side-panel__section side-panel__empty"
          >
            本日の予約はありません
          </div>
          <div
            v-else
            class="side-panel__section"
          >
            <div class="reservation-panel__list">
              <div
                v-for="reservation in todayReservations"
                :key="`floor-rsv-${reservation.id}`"
                class="reservation-panel__item"
                :class="{ 'reservation-panel__item--linked': reservation.status === 'checked_in' }"
                @click="toggleReservationDetail(reservation)"
              >
                <div class="reservation-panel__main">
                  <span class="reservation-panel__time">{{ reservation.time }}</span>
                  <span class="reservation-panel__count">{{ reservation.count }}名</span>
                  <v-icon
                    size="14"
                    class="reservation-panel__caret"
                    :class="{ 'reservation-panel__caret--open': expandedReservationId === reservation.id }"
                  >
                    {{ icons.mdiChevronDown }}
                  </v-icon>
                  <v-spacer />
                  <!-- この予約から卓作成済み（来店中）であることを明示する -->
                  <span
                    v-if="reservation.status === 'checked_in'"
                    class="reservation-panel__linked"
                    :title="`この予約から卓を作成済みです${reservation.table ? `（${reservation.table}）` : ''}`"
                  >
                    ✓ 卓作成済み
                  </span>
                  <span
                    v-if="reservation.status === 'confirmed' && reservation.missingItems.length > 0"
                    class="reservation-panel__warn"
                    title="日別予約で予約を開いてコースを入力すると卓を作成できます"
                  >
                    ⚠ 要{{ reservation.missingItems.join('・') }}
                  </span>
                </div>
                <div
                  v-if="reservation.table || reservation.label"
                  class="reservation-panel__sub"
                >
                  <span
                    v-if="reservation.table"
                    class="reservation-panel__table"
                  >{{ reservation.table }}</span>
                  <span
                    v-if="reservation.label"
                    class="reservation-panel__label text-truncate"
                  >{{ reservation.label }}</span>
                </div>
                <!-- 行タップで予約内容を確認できる -->
                <div
                  v-if="expandedReservationId === reservation.id"
                  class="reservation-panel__detail"
                >
                  <div class="reservation-panel__detail-row">
                    <span class="reservation-panel__detail-key">時間</span>
                    <span>
                      {{ reservation.time }}〜{{ reservation.endTime || '' }}
                      <span
                        v-if="!reservation.endTime"
                        class="reservation-panel__detail-note"
                      >（終了未設定）</span>
                    </span>
                  </div>
                  <div class="reservation-panel__detail-row">
                    <span class="reservation-panel__detail-key">コース</span>
                    <span>{{ reservation.courseName || '未選択' }}</span>
                  </div>
                  <div class="reservation-panel__detail-row">
                    <span class="reservation-panel__detail-key">指名</span>
                    <span>{{ reservation.nominationText || 'なし' }}</span>
                  </div>
                  <div class="reservation-panel__detail-row">
                    <span class="reservation-panel__detail-key">顧客</span>
                    <span>{{ reservation.customersText || '顧客未設定' }}</span>
                  </div>
                  <div
                    v-if="reservation.memo"
                    class="reservation-panel__detail-row"
                  >
                    <span class="reservation-panel__detail-key">メモ</span>
                    <span>{{ reservation.memo }}</span>
                  </div>
                  <template v-if="reservation.status === 'confirmed'">
                    <v-btn
                      small
                      outlined
                      color="success"
                      block
                      class="mt-2"
                      :disabled="reservation.missingItems.length > 0"
                      @click.stop="checkInFromFloor(reservation)"
                    >
                      <v-icon
                        small
                        left
                      >
                        {{ icons.mdiSofaSingle }}
                      </v-icon>
                      この予約から卓を作成
                    </v-btn>
                    <div
                      v-if="reservation.missingItems.length > 0"
                      class="reservation-panel__detail-hint"
                    >
                      コースが未選択のため卓を作成できません
                    </div>
                    <v-btn
                      small
                      :outlined="reservation.missingItems.length === 0"
                      :color="reservation.missingItems.length > 0 ? 'warning' : undefined"
                      block
                      class="mt-1"
                      @click.stop="editReservationFromFloor(reservation)"
                    >
                      {{ reservation.missingItems.length > 0 ? '予約を編集してコースを選択' : '予約を編集' }}
                    </v-btn>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 卓時間タブ -->
        <template v-else>
          <div
            v-if="tableTimeSummary.length === 0"
            class="side-panel__section side-panel__empty"
          >
            セット中の卓はありません
          </div>
          <div
            v-else
            class="side-panel__section"
          >
            <div class="table-time__list">
              <div
                v-for="item in tableTimeSummary"
                :key="`time-${item.hallId}-${item.tableNumber}`"
                class="table-time__item"
                :class="{ 'table-time__item--warn': item.isExpiring }"
              >
                <div class="table-time__left">
                  <span class="table-time__name">{{ item.tableName }}</span>
                  <span
                    v-if="item.castNames"
                    class="table-time__casts"
                  >
                    {{ item.castNames }}
                  </span>
                </div>
                <span class="table-time__remaining">{{ item.remaining }}</span>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div
      v-if="!isLoading && halls.length > 0 && !isLayoutConfigured"
      class="floor-empty-state"
    >
      <div class="floor-empty-state__inner">
        <v-icon
          size="64"
          class="floor-empty-state__icon"
        >
          mdi-floor-plan
        </v-icon>
        <div class="floor-empty-state__title text-h5">
          フロアレイアウトが未設定です
        </div>
        <p class="floor-empty-state__description">
          フロアマップを表示するには、まずゾーン（ホール）と卓の配置を設定する必要があります。<br>
          設定画面でゾーンと卓をドラッグ＆ドロップで並べ、保存してください。
        </p>
        <ol class="floor-empty-state__steps">
          <li>ゾーンを画面に追加して位置・サイズを調整</li>
          <li>卓をドラッグして配置</li>
          <li>「保存」ボタンで確定</li>
        </ol>
        <v-btn
          color="primary"
          large
          @click="goToFloorLayoutSettings"
        >
          <v-icon left>
            mdi-cog
          </v-icon>
          フロアレイアウトを設定する
        </v-btn>
      </div>
    </div>

    <!-- 予約からの卓作成ウィザード（ホール → 卓 → 確認・修正。日別予約と同じ） -->
    <reservation-check-in-wizard
      v-model="checkInWizardOpen"
      :reservation="checkInWizardReservation"
      :halls="wizardHalls"
      :casts="floorCasts"
      :courses="courses"
      :nominations="nominations"
      :day-reservations="todayReservationRows"
      @completed="onCheckInWizardCompleted"
      @error="onCheckInWizardError"
    />

    <!-- 予約編集ダイアログ（要コース時の入力導線。日別予約と同じ） -->
    <reservation-dialog
      v-model="reservationDialogOpen"
      :reservation="reservationDialogReservation"
      :casts="floorCasts"
      :courses="courses"
      :referrals="referrals"
      :nominations="nominations"
      :customer-tags="customerTags"
      @saved="onReservationDialogSaved"
      @check-in-requested="onDialogCheckInRequested"
      @error="onCheckInWizardError"
    />

    <!-- 既存の TableShowDrawer / TableCreateDrawer -->
    <template v-if="selectedLayout">
      <table-show-drawer
        v-if="selectedLayout._table && selectedLayout._table.hold === undefined && targetTableActions.show"
        :is-visible="targetTableActions.show"
        :table-id="selectedLayout._table.id"
        :table-number="selectedLayout.table_number"
        :table-prefix="selectedHall.attributes.tablePrefix"
        :table-suffix="selectedHall.attributes.tableSuffix"
        :custom-name="selectedLayout.name || ''"
        :sequence-number="selectedLayout._table.attributes.sequenceNumber"
        :start-at="selectedLayout._table.attributes.startAt"
        :today="today"
        :customer-count="selectedLayout._table.attributes.customerCount"
        :subtotal="selectedLayout._table.meta.subtotal"
        :total="selectedLayout._table.meta.total"
        :paid-in-advance-amount="selectedLayout._table.meta.paidInAdvanceAmount"
        :expire-at="selectedLayout._table.meta.expireAt"
        :current-nominations="selectedLayout._table.meta.currentNominations.data"
        :course-overview="selectedLayout._table.meta.courseOverview.data"
        :customer-table-taggings="selectedLayout._table.attributes.customerTableTaggings.data"
        :default-tab-idx="0"
        :courses="selectedHall.attributes.tableInitConfig.data.attributes.courses.data"
        :self-order-status="selectedLayout._table.attributes.selfOrderStatus"
        :auto-extension-enabled="selectedLayout._table.attributes.autoExtensionEnabled"
        :auto-extended-at="selectedLayout._table.attributes.autoExtendedAt"
        :hall-id="selectedHall.id"
        :hall-name="selectedHall.attributes.name"
        :checkout-config="hallCheckoutConfig"
        :initial-deposits="selectedLayout._table.meta.deposits || []"
        :total-deposit-amount="selectedLayout._table.meta.totalDepositAmount || 0"
        :customers="selectedLayout._table.attributes.customers || []"
        @updated="updateTable($event)"
        @checked="checkoutTable(halls.indexOf(selectedHall), $event)"
        @close="closeDrawer({ strategy: null })"
      />

      <table-create-drawer
        v-if="!selectedLayout._table && targetTableActions.create && !useTableCreateWizard"
        :is-visible="targetTableActions.create"
        :hall-id="selectedHall.id"
        :table-number="selectedLayout.table_number"
        :table-prefix="selectedHall.attributes.tablePrefix"
        :table-suffix="selectedHall.attributes.tableSuffix"
        :custom-name="selectedLayout.name || ''"
        :referrals="selectedHall.attributes.tableInitConfig.data.attributes.referrals.data"
        :nominations="selectedHall.attributes.tableInitConfig.data.attributes.nominations.data"
        :courses="selectedHall.attributes.tableInitConfig.data.attributes.courses.data"
        :items="selectedHall.attributes.tableInitConfig.data.attributes.items.data"
        @close="closeDrawer({ strategy: actionType.delete })"
        @created="addTable($event)"
      />

      <table-create-wizard
        v-if="!selectedLayout._table && targetTableActions.create && useTableCreateWizard"
        :value="targetTableActions.create"
        :hall-id="selectedHall.id"
        :table-number="selectedLayout.table_number"
        :table-prefix="selectedHall.attributes.tablePrefix"
        :table-suffix="selectedHall.attributes.tableSuffix"
        :custom-name="selectedLayout.name || ''"
        :referrals="normalizeForWizard(selectedHall.attributes.tableInitConfig.data.attributes.referrals.data)"
        :nominations="normalizeForWizard(selectedHall.attributes.tableInitConfig.data.attributes.nominations.data)"
        :courses="normalizeForWizard(selectedHall.attributes.tableInitConfig.data.attributes.courses.data)"
        :items="normalizeForWizard(selectedHall.attributes.tableInitConfig.data.attributes.items.data)"
        :users="normalizeForWizard(users)"
        :checkout-config="hallCheckoutConfig"
        :vending-contribution-triggers="vendingContributionTriggers"
        @close="closeDrawer({ strategy: actionType.delete })"
        @created="addTable($event)"
      />
    </template>

    <!-- 卓の連結ダイアログ(親卓のチップ/連結ボタンから) -->
    <table-group-dialog
      v-model="tableGroupDialog.show"
      :parent-table="tableGroupDialog.table"
      :hall="tableGroupDialog.hall"
      :existing-group="tableGroupDialog.group"
      @saved="onTableGroupSaved"
      @released="onTableGroupReleased"
    />

    <!-- 子卓の連結解除ダイアログ -->
    <v-dialog
      v-model="unlinkDialog.show"
      max-width="400"
    >
      <v-card>
        <v-toolbar
          flat
          color="transparent"
        >
          {{ unlinkDialog.tableName }} — {{ unlinkDialog.parentName }}と連結中
        </v-toolbar>
        <v-card-text>
          この卓の連結を解除しますか？解除すると {{ unlinkDialog.tableName }} で新規入店を受け付けられるようになります。
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            text
            @click="unlinkDialog.show = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            @click="confirmUnlink"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiLinkVariantOff }}
            </v-icon>
            この卓の連結を解除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- キャスト解除ダイアログ（複数選択対応） -->
    <v-dialog
      v-model="removeDialog.show"
      max-width="480"
      @click:outside="removeDialog.show = false"
    >
      <v-card class="pa-4">
        <v-card-title class="text-subtitle-1 pa-0 mb-3">
          キャストを解除
        </v-card-title>

        <div
          v-if="removeDialog.casts.length > 0"
          class="d-flex align-center mb-2"
        >
          <v-btn
            text
            small
            @click="toggleAllRemoveSelection"
          >
            {{ removeDialog.selectedKeys.length === removeDialog.casts.length ? '全て解除' : '全て選択' }}
          </v-btn>
          <v-spacer />
          <span class="text-caption grey--text">
            {{ removeDialog.selectedKeys.length }} / {{ removeDialog.casts.length }} 選択中
          </span>
        </div>

        <v-card-text class="pa-0 mb-3">
          <div
            v-for="cast in removeDialog.casts"
            :key="`remove-${cast.type}-${cast.userId}`"
            class="remove-dialog__item"
            :style="{ borderLeftColor: getCastColor(cast) }"
            @click="
              removeDialog.selectedKeys.includes(`${cast.type}:${cast.userId}`)
                ? removeDialog.selectedKeys = removeDialog.selectedKeys.filter(k => k !== `${cast.type}:${cast.userId}`)
                : removeDialog.selectedKeys = [...removeDialog.selectedKeys, `${cast.type}:${cast.userId}`]
            "
          >
            <v-checkbox
              :input-value="removeDialog.selectedKeys.includes(`${cast.type}:${cast.userId}`)"
              hide-details
              dense
              class="mt-0 pt-0 mr-2"
              readonly
            />
            <div class="remove-dialog__item-body">
              <div class="remove-dialog__item-name">
                {{ cast.name }}
              </div>
              <div class="remove-dialog__item-type text-caption grey--text">
                {{ cast.type === 'nomination' ? cast.vendibleName : 'フリー' }}
              </div>
            </div>
          </div>
          <div
            v-if="removeDialog.casts.length === 0"
            class="text-caption text-center grey--text py-4"
          >
            解除可能なキャストがいません
          </div>
        </v-card-text>

        <v-card-text
          v-if="removeDialog.casts.some(c => removeDialog.selectedKeys.includes(`${c.type}:${c.userId}`) && c.type === 'nomination')"
          class="pa-0 mb-3 text-caption"
          style="color: #f59e0b;"
        >
          ※ 指名オーダーも削除されます
        </v-card-text>

        <v-card-actions class="pa-0">
          <v-spacer />
          <v-btn
            text
            :disabled="removeDialog.processing"
            @click="removeDialog.show = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="error"
            :disabled="removeDialog.selectedKeys.length === 0"
            :loading="removeDialog.processing"
            @click="confirmRemoveCast"
          >
            解除
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- キャスト配置タイプ選択ダイアログ -->
    <v-dialog
      v-model="dropDialog.show"
      max-width="320"
      @click:outside="cancelDrop"
    >
      <v-card class="pa-4">
        <v-card-title class="text-subtitle-1 pa-0 mb-4">
          {{ dropDialog.castName }} → {{ dropDialog.tableName }}
        </v-card-title>
        <v-card-text class="pa-0">
          <v-btn
            block
            large
            color="#14b8a6"
            dark
            class="mb-3"
            @click="confirmDrop('free')"
          >
            <v-icon
              left
              size="20"
            >
              mdi-account-arrow-right
            </v-icon>
            フリー
          </v-btn>
          <v-btn
            v-for="nomination in nominations"
            :key="`nom-drop-${nomination.id}`"
            block
            large
            :color="nomination.attributes.color || 'primary'"
            dark
            class="mb-3"
            @click="confirmDrop('nomination', nomination)"
          >
            <v-icon
              left
              size="20"
            >
              mdi-star
            </v-icon>
            {{ nomination.attributes.name }}
          </v-btn>
          <div
            v-if="!nominations || nominations.length === 0"
            class="text-caption text-center grey--text"
          >
            指名メニューが設定されていません
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- 勤怠ステータス設定ダイアログ -->
    <v-dialog
      v-model="statusDialog.show"
      max-width="360"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ statusDialog.castName }} の勤怠ステータス
        </v-card-title>
        <v-card-text>
          <p
            v-if="statusDialog.mode === 'create'"
            class="text-caption text--secondary mb-3"
          >
            選択したステータスでタイムカードを作成します
          </p>
          <v-select
            v-model="statusDialog.selectedStatusId"
            :items="timecardStatuses"
            item-text="name"
            item-value="id"
            label="ステータス"
            hide-details
            class="pt-1"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-btn
            v-if="statusDialog.mode === 'update' && statusDialog.currentStatusId"
            text
            color="error"
            :disabled="statusDialog.submitting"
            @click="clearCastStatus"
          >
            解除
          </v-btn>
          <v-btn
            v-if="statusDialog.mode === 'update' && statusDialog.currentStatusId"
            text
            color="primary"
            :disabled="statusDialog.submitting"
            @click="switchStatusDialogToCheckIn"
          >
            出勤にする
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="statusDialog.submitting"
            @click="statusDialog.show = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="statusDialog.submitting"
            :disabled="!statusDialog.selectedStatusId"
            @click="submitStatusDialog"
          >
            確定
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 出勤時刻の登録（卓作成ウィザードと同じ5分刻みドラムピッカー） -->
    <v-dialog
      v-model="checkInTimeDialog.show"
      max-width="360"
    >
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ checkInTimeDialog.castName }} の出勤時刻
        </v-card-title>
        <v-card-text>
          <time-drum-picker v-model="checkInTimeDialog.value" />
          <p class="text-caption dialog-muted mt-2 mb-0">
            5分単位で選べます（初期値は現在時刻）
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="checkInTimeDialog.submitting"
            @click="checkInTimeDialog.show = false"
          >
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            :loading="checkInTimeDialog.submitting"
            @click="confirmCheckInTime"
          >
            この時刻で出勤
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <QrCode
      v-if="receipt.id && receipt.attributes.receiptImages.length > 0"
      :key="`receipt-${receipt.id}`"
      :urls="receipt.attributes.receiptImages"
      :immediate-share="true"
      class="mt-1"
      @closed="receipt = {}"
    />
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  watch,
  provide,
  inject,
  getCurrentInstance,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api'
import {
  find,
  get,
  uniqBy,
  flatMap,
  findIndex,
  sumBy,
  groupBy,
  orderBy,
} from 'lodash'
import { useQrCodeSlipPrint } from '@yonarezi/yonarezi3.slip-print'
import { format, formatISO, parseISO } from 'date-fns'
import {
  mdiAlert, mdiLinkVariant, mdiLinkVariantOff, mdiChevronDown, mdiSofaSingle,
} from '@mdi/js'
import materialColors from 'vuetify/lib/util/colors'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import HallApi from '@/api/waiter/Hall'
import TableGroupDialog from '@/views/components/table/TableGroupDialog.vue'
import useTableGroups from '@/views/composable/useTableGroups'
import { tableGroupColor } from '@/utils/tableGroupColors'
import UserApi from '@/api/waiter/User'
import ReservationCheckInWizard from '@/views/waiter/reservations/components/ReservationCheckInWizard.vue'
import ReservationDialog from '@/views/waiter/reservations/components/ReservationDialog.vue'
import TimeDrumPicker from '@/views/components/time/TimeDrumPicker.vue'
import CustomerTagApi from '@/api/waiter/CustomerTag'
import { checkInMissingItems } from '@/views/waiter/reservations/reservationDisplayUtils'
import VendibleApi from '@/api/waiter/Vendible'
import ReferralApi from '@/api/waiter/Referral'
import VendingContributionTriggerApi from '@/api/waiter/VendingContributionTrigger'
import TimecardApi from '@/api/waiter/Timecard'
import TimecardStatusApi from '@/api/waiter/TimecardStatus'
import TableApi from '@/api/waiter/Table'
import ReservationApi from '@/api/waiter/Reservation'
import CustomerApi from '@/api/v2/admin/customer-management/Customer'
import TableCreateDrawer from '@/views/components/table/TableCreateDrawer.vue'
import TableCreateWizard from '@/views/components/table/TableCreateWizard/TableCreateWizard.vue'
import useFeatureFlags from '@/views/composable/useFeatureFlags'
import TableShowDrawer from '@/views/components/table/TableShowDrawer/TableShowDrawer.vue'
import useCurrentData from '@/views/composable/useCurrentData'
import useTimecard from '@/views/composable/useTimecard'
import useCompInit from '@/views/composable/useCompInit'
import { useDateRange } from '@/views/v2-temp/common/composables/useDateRange'
import { cable } from '@/plugins/websocket-client'
import QrCode from '@/views/components/printer/QrCode.vue'
import AutoExtensionBadge from '@/views/components/table/AutoExtensionBadge.vue'
import {
  formatElapsed as _formatElapsed,
  formatRemainingSigned as _formatRemainingSigned,
  isExpiringSoon as _isExpiringSoon,
  isOvertime as _isOvertime,
  getRemainingTimeStatus as _getRemainingTimeStatus,
  formatTime,
  formatTimeRange,
  getTableCasts as _getTableCasts,
  hasCautionCustomer,
  splitCastsForDisplay,
  buildFreeCastUserIds,
  buildNominationVendingHistoryParams,
  parseStoredCompactMode,
  getFinishNoticeStatus,
  isFloorLayoutConfigured,
  isNarrowLayout,
  isActiveTimecard,
  isCast,
  parseStoredShowAllRoles,
  getTimecardStatusName,
  groupCastsByStatus,
  getBusinessTodayStr,
  formatShiftRange,
  isPastScheduledStart,
} from './floorUtils'
import { removePortFromIpAddress } from '@/utils/removePortFromIpAddress'
import { reportPrinterError, wrapPrinterReceive } from '@/utils/sentryReportPrinterError'
import { canExecutePrintOnThisDevice } from '@/views/composable/useReceiptPrinterDeviceSetting'
import useSdpSlipPrint from '@/views/composable/useSdpSlipPrint'

const CANVAS_WIDTH = 1200
const CANVAS_HEIGHT = 800

export default {
  components: {
    TableGroupDialog,
    TableCreateDrawer,
    TableCreateWizard,
    TableShowDrawer,
    QrCode,
    AutoExtensionBadge,
    ReservationCheckInWizard,
    ReservationDialog,
    TimeDrumPicker,
  },
  setup() {
    const vm = getCurrentInstance().proxy

    const { flags: featureFlags } = useFeatureFlags()
    const useTableCreateWizard = computed(() => featureFlags.value.useTableCreateWizard)

    const normalizeForWizard = list => (list || []).map(record => ({
      id: record.id,
      ...record.attributes,
    }))

    const { currentUser, currentClub } = useCurrentData()
    const { simpleCurrentTime } = useTimecard()
    const { id: currentUserId } = currentUser.value
    // エリア制: タブ不要、全ホールを1キャンバスに表示
    const wideMode = ref(false)
    const compactMode = ref(parseStoredCompactMode(localStorage.getItem('floorCompactMode')))
    watch(compactMode, val => {
      localStorage.setItem('floorCompactMode', String(val))
    })
    const showAllRoles = ref(parseStoredShowAllRoles(localStorage.getItem('floorShowAllRoles')))
    watch(showAllRoles, val => {
      localStorage.setItem('floorShowAllRoles', String(val))
    })
    const savedZoom = localStorage.getItem('floorZoomLevel')
    const zoomLevel = ref(savedZoom ? Number(savedZoom) : 1)

    const saveZoom = () => {
      localStorage.setItem('floorZoomLevel', String(zoomLevel.value))
    }
    const zoomIn = () => {
      zoomLevel.value = Math.min(2, Math.round((zoomLevel.value + 0.05) * 20) / 20)
      saveZoom()
    }
    const zoomOut = () => {
      zoomLevel.value = Math.max(0.3, Math.round((zoomLevel.value - 0.05) * 20) / 20)
      saveZoom()
    }

    // ワイドモードは <html> に floor-wide-mode クラスを付け、
    // 下部の global CSS で Vuetify の navigation-drawer / app-bar / main padding を
    // 制御する。インライン style の保存/復元が不要になり、Vuetify の更新で
    // インライン style が変わっても影響を受けない。
    const WIDE_MODE_CLASS = 'floor-wide-mode'
    const toggleWideMode = () => {
      wideMode.value = !wideMode.value
      document.documentElement.classList.toggle(WIDE_MODE_CLASS, wideMode.value)
    }
    // エリア制: 常に全ホール表示
    const selectedLayoutIdx = ref(null)
    const targetTableActions = reactive({
      create: false, show: false, created: false, updated: false,
    })
    const halls = ref([])

    // 卓の連結(卓グループ)
    const tableGroups = useTableGroups({ halls, vm, currentUserId })
    const childGroupParentName = layout => (
      layout._childGroup ? tableGroups.tableNameOf(layout._hall, layout._childGroup.attributes.parentTableNumber) : ''
    )
    const openTableGroupFromFloor = layout => {
      if (!layout._table) return
      const hallIdx = halls.value.findIndex(h => Number(h.id) === Number(layout._hall?.id))
      tableGroups.openTableGroup(hallIdx, layout._table)
    }
    // コンパクト表示: 枠線は状態色専用なので、上辺の帯とティントでグループ色を出す(マップ表示と同じ方針)
    const compactCardStyle = layout => {
      if (!layout._groupColor) return null
      const style = { borderTop: `4px solid ${layout._groupColor}` }
      if (layout._childGroup) style.backgroundColor = `${layout._groupColor}29`

      return style
    }
    const unlinkDialog = reactive({
      show: false,
      hallIdx: null,
      tableNumber: null,
      tableName: '',
      parentName: '',
    })
    const confirmUnlink = async () => {
      await tableGroups.unlinkTableGroup(unlinkDialog.hallIdx, unlinkDialog.tableNumber)
      unlinkDialog.show = false
    }
    const actionType = {
      new: 'new',
      create: 'create',
      delete: 'delete',
      confirm: 'confirm',
    }

    const colorCodes = {
      red: '#D74A4A',
      yellow: '#F4C645',
    }

    const tableFinishNoticeColors = ref({})

    const { isLoading, initWith } = useCompInit()
    const currentClubMeta = inject('currentClubMeta', {})
    const getClub = inject('getClub', () => {})
    const fetchPlusMinusStats = inject('fetchPlusMinusStats', () => {})
    const fetchOperationRateStats = inject('fetchOperationRateStats', () => {})
    const { today } = useDateRange()

    const users = ref([])
    const vendibles = ref([])
    const courses = ref([])
    const nominations = ref([])
    const items = ref([])
    const referrals = ref([])
    const vendingContributionTriggers = ref([])
    const timecards = ref([])
    const receipt = ref({})
    const workSchedules = ref([])

    provide('users', users)
    provide('vendibles', vendibles)
    provide('courses', courses)
    provide('nominations', nominations)
    provide('items', items)
    provide('referrals', referrals)
    provide('vendingContributionTriggers', vendingContributionTriggers)
    provide('timecards', timecards)
    provide('halls', halls)

    // ── WebSocket ──
    // NOTE: receivedハンドラは同じ通知の再送(confirm再通知・再接続refetchとの競合)があるため、
    // 冪等に書くこと。findIndexの-1をそのままspliceに渡すと末尾要素を誤操作するので必ずガードする
    const otherWaiterUsesTable = (hallIdx, tableNumber, fromUserId) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return
        const targetHallNumber = hallIdx + 1
        const targetTableNumber = tableNumber + 1
        const tables = targetHall.attributes.tables.data
        // 同番号の卓(hold含む)が既にあれば何もしない(hold重複の防止)
        if (tables.some(table => Number(table.attributes.tableNumber) === Number(targetTableNumber))) return
        tables.push({
          attributes: { hallId: targetHallNumber, tableNumber: targetTableNumber },
          id: String(targetTableNumber),
          hold: true,
          meta: {},
        })
      }
    }

    // 同番号の残存hold(他ウェイター作成中のダミー卓)を取り除く
    const sweepHoldTables = (tables, tableNumber) => {
      for (let i = tables.length - 1; i >= 0; i -= 1) {
        if (tables[i].hold !== undefined && Number(tables[i].attributes.tableNumber) === Number(tableNumber)) {
          tables.splice(i, 1)
        }
      }
    }

    // NOTE: 'delete'は卓作成キャンセル(hold削除)と会計(実卓削除)の両方で届く
    const releaseOtherWaiterUsesTable = (hallIdx, tableNumber, fromUserId) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return
        const tables = targetHall.attributes.tables.data
        const tableIdx = findIndex(
          tables,
          table => Number(table.attributes.tableNumber) === Number(tableNumber),
        )
        if (tableIdx >= 0) tables.splice(tableIdx, 1)
        sweepHoldTables(tables, tableNumber)
      }
    }

    // 実卓のupsert: id一致で置換 → なければ同番号holdを置換 → なければ追加。残存holdは掃除
    const upsertOtherWaiterTable = (targetHall, tableData) => {
      const tables = targetHall.attributes.tables.data
      const realIdx = findIndex(
        tables,
        table => table.hold === undefined && Number(table.id) === Number(tableData.id),
      )
      if (realIdx >= 0) {
        tables.splice(realIdx, 1, tableData)
      } else {
        const holdIdx = findIndex(
          tables,
          table => table.hold !== undefined && Number(table.attributes.tableNumber) === Number(tableData.attributes.tableNumber),
        )
        if (holdIdx >= 0) {
          tables.splice(holdIdx, 1, tableData)
        } else {
          tables.push(tableData)
        }
      }
      sweepHoldTables(tables, tableData.attributes.tableNumber)
    }

    const updateOtherWaiterTable = ({ hallIdx, fromUserId, tableData }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return
        upsertOtherWaiterTable(targetHall, tableData)
      }
    }

    const addOtherWaiterTable = ({ hallIdx, tableData, fromUserId }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return
        upsertOtherWaiterTable(targetHall, tableData)
      }
    }

    const removeOtherWaiterTable = ({ hallIdx, tableId, fromUserId }) => {
      if (fromUserId !== currentUserId) {
        const targetHall = halls.value[hallIdx]
        if (!targetHall?.attributes) return
        const tableIdx = findIndex(
          targetHall.attributes.tables.data,
          table => Number(table.id) === Number(tableId),
        )
        if (tableIdx >= 0) {
          targetHall.attributes.tables.data.splice(tableIdx, 1)
        }
      }
    }

    const reservedOtherWaiterTable = fromUserId => {
      if (fromUserId !== currentUserId && targetTableActions.create) {
        // eslint-disable-next-line no-use-before-define
        const layout = currentLayouts.value[selectedLayoutIdx.value]
        // layout不明のまま0番を送ると他端末に幽霊hold(#1)が生まれるため通知しない
        if (!layout) return
        const hIdx = halls.value.findIndex(h => Number(h.id) === Number(layout._hall?.id))
        if (hIdx < 0) return
        // eslint-disable-next-line no-use-before-define
        subscription.occupyPlace(hIdx, layout.table_number - 1, 'new')
      }
    }

    const syncTables = hallIdx => {
      // eslint-disable-next-line no-use-before-define
      subscription.confirmReservedOccupyPlace(hallIdx)
    }

    vm.$store.commit('websocket/REGISTER_CHANNEL', 'hall')

    const subscription = cable.subscriptions.create(
      // Halls.vueも同じHallChannelを購読するため、identifierをページ単位で区別する(バックエンドはparams不使用)
      { channel: 'HallChannel', view: 'floor' },
      {
        connected: () => { vm.$store.dispatch('websocket/channelConnected', 'hall') },
        disconnected: () => { vm.$store.dispatch('websocket/channelDisconnected', 'hall') },
        received: payload => {
          const {
            hallIdx, tableNumber, tableId, fromUserId, strategy, tableData,
          } = payload
          // 卓の連結(upsert/release)は自端末の操作分も含めてここで同期する
          // eslint-disable-next-line no-use-before-define
          if (tableGroups.handleChannelMessage(payload)) return

          switch (strategy) {
            case 'new': otherWaiterUsesTable(hallIdx, tableNumber, fromUserId); break
            case 'updateTable': updateOtherWaiterTable({ hallIdx, tableData, fromUserId }); break
            case 'addTable': addOtherWaiterTable({ hallIdx, tableData, fromUserId }); break
            case 'removeTable': removeOtherWaiterTable({ hallIdx, tableId, fromUserId }); break
            case 'delete': releaseOtherWaiterUsesTable(hallIdx, tableNumber, fromUserId); break
            case 'confirm': reservedOtherWaiterTable(fromUserId); break
            default: break
          }
        },
        occupyPlace: (hallIdx, tableNumber, strategy) => {
          return subscription.perform('occupy_place', {
            hallIdx, tableNumber, currentUserId, strategy,
          })
        },
        deleteOccupyPlace: ({ hallIdx, tableNumber }) => {
          return subscription.perform('delete_occupy_place', { hallIdx, tableNumber, currentUserId })
        },
        confirmReservedOccupyPlace: hallIdx => {
          return subscription.perform('confirm_reserved_occupy_place', { hallIdx, currentUserId })
        },
        updateTable: ({ hallIdx, tableData }) => {
          return subscription.perform('update_table', { hallIdx, currentUserId, tableData })
        },
        addTable: ({ hallIdx, tableData }) => {
          return subscription.perform('add_table', { hallIdx, currentUserId, tableData })
        },
        removeTable: ({ hallIdx, tableId }) => {
          return subscription.perform('remove_table', { hallIdx, tableId, currentUserId })
        },
      },
    )

    const vendibleTags = computed(() => {
      return uniqBy(orderBy(flatMap(vendibles.value, 'attributes.tags.data'), 'attributes.position'), 'id')
    })
    provide('vendibleTags', vendibleTags)

    const usersWithTimecard = computed(() => {
      const sortedTimecards = [...timecards.value].sort((a, b) => {
        return b.attributes.startAt.localeCompare(a.attributes.startAt)
      })

      return users.value
        .map(user => {
          const userLatestTimecard = sortedTimecards.find(timecard => +timecard.attributes.userId === +user.id)

          return userLatestTimecard ? { ...user, timecard: userLatestTimecard.attributes } : null
        })
        .filter(user => user !== null)
    })
    provide('usersWithTimecard', usersWithTimecard)

    // ── Data loading ──
    const getHalls = async () => {
      const res = await HallApi.getHalls({ isOperation: true })
      if (res?.data) { halls.value = [...res.data.halls.data] }
    }

    const hallConnected = computed(() => vm.$store.state.websocket.channels.hall?.connected)
    let prevHallConnected = null
    watch(hallConnected, newVal => {
      if (prevHallConnected === false && newVal === true) {
        getHalls().catch(err => console.error('[WebSocket] Failed to refetch halls:', err))
      }
      prevHallConnected = newVal
    })

    const getUsers = async () => {
      const res = await UserApi.getUsers()
      if (res?.data) { users.value = [...res.data.users.data] }
    }
    const getVendibles = async () => {
      const res = await VendibleApi.getVendibles()
      if (res?.data) {
        vendibles.value = [...res.data.vendibles.data]
        const vendiblesByType = groupBy(vendibles.value, 'attributes.vendibleType')
        courses.value = vendiblesByType.Course
        nominations.value = vendiblesByType.Nomination
        items.value = vendiblesByType.Item
      }
    }
    const getReferrals = async () => {
      const res = await ReferralApi.getReferrals()
      if (res?.data) { referrals.value = [...res.data.referrals.data] }
    }
    // 予約編集ダイアログ（コース未選択の解消導線）用
    const customerTags = ref([])
    const getCustomerTags = async () => {
      const res = await CustomerTagApi.getCustomerTags()
      if (res?.data) { customerTags.value = [...(res.data.customerTags?.data || [])] }
    }
    const getVendingContributionTriggers = async () => {
      const res = await VendingContributionTriggerApi.getVendingContributionTriggers()
      if (res?.data) { vendingContributionTriggers.value = [...res.data.vendingContributionTriggers.data] }
    }
    const getTimecards = async () => {
      const res = await TimecardApi.getTimecards({ isToday: true })
      if (res?.data) { timecards.value = [...res.data.timecards.data] }
    }
    // 本日（営業日）の予約。取得できない場合は非表示で degrade
    const todayReservations = ref([])
    const getTodayReservations = async () => {
      try {
        const businessToday = getBusinessTodayStr(new Date(), currentClub.value?.openAt)
        const res = await ReservationApi.getReservations({ date: businessToday })
        const rows = res?.data?.reservations?.data || []
        todayReservations.value = rows
          .filter(r => !r.attributes.canceledAt)
          .map(r => {
            const attrs = r.attributes
            const timeMatch = String(attrs.startTime || '').match(/(\d{2}):(\d{2})/)
            const endMatch = String(attrs.endTime || '').match(/(\d{2}):(\d{2})/)
            const reservationNominations = attrs.nominations || []
            const customers = attrs.customers || []
            let label = ''
            if (reservationNominations.length > 0) {
              label = `指名: ${reservationNominations.map(n => n.userName).join('・')}`
            } else if (customers.length > 0) {
              label = customers.map(c => c.name).join('・')
            }

            return {
              id: r.id,
              time: timeMatch ? `${timeMatch[1]}:${timeMatch[2]}` : '未定',
              endTime: endMatch ? `${endMatch[1]}:${endMatch[2]}` : null,
              count: attrs.customerCount || 0,
              table: attrs.hall ? `${attrs.hall.name}${attrs.tableNumber ? ` ${attrs.tableNumber}` : ''}` : '',
              label,
              status: attrs.status,
              courseName: attrs.course?.name || null,
              nominationText: reservationNominations.map(n => `${n.userName}（${n.nominationName}）`).join('・'),
              customersText: customers.map(c => c.name).join('・'),
              memo: attrs.memo || '',
              missingItems: checkInMissingItems(attrs),
              raw: r,
            }
          })
          .sort((a, b) => {
            // 深夜（〜5時台）は翌側として最後に並べる（日別予約ボードと同じ規約）
            const key = t => {
              const hour = Number(String(t).slice(0, 2))
              if (Number.isNaN(hour)) return 99
              if (hour < 6) return hour + 24

              return hour
            }
            const diff = key(a.time) - key(b.time)
            if (diff !== 0) return diff

            return a.time < b.time ? -1 : 1
          })
      } catch (error) {
        todayReservations.value = []
      }
    }

    // 予約詳細の開閉（行タップで予約内容を確認できる）
    const expandedReservationId = ref(null)
    const toggleReservationDetail = reservation => {
      expandedReservationId.value = expandedReservationId.value === reservation.id ? null : reservation.id
    }

    // 予約からの卓作成ウィザード（日別予約と同じコンポーネントをフロアにも載せる）
    const checkInWizardOpen = ref(false)
    const checkInWizardReservation = ref(null)
    const floorCasts = computed(() => users.value.filter(u => u.attributes?.role === 'cast'))
    // ウィザード用にフロアの halls(isOperation) を軽量形へ正規化
    // （attributes.tables は before_check_out 卓 = 稼働中）
    const wizardHalls = computed(() => halls.value.map(h => ({
      id: Number(h.id),
      name: h.attributes?.name,
      tableCount: h.attributes?.tableCount || 0,
      tablePrefix: h.attributes?.tablePrefix || '',
      tableSuffix: h.attributes?.tableSuffix || '',
      busyTableNumbers: (h.attributes?.tables?.data || []).map(t => Number(t.attributes?.tableNumber)),
    })))
    const todayReservationRows = computed(() => todayReservations.value.map(r => r.raw))
    const checkInFromFloor = reservation => {
      checkInWizardReservation.value = reservation.raw
      // 予約propの反映をウィザードのopenより先に確定させる
      nextTick(() => {
        checkInWizardOpen.value = true
      })
    }
    const onCheckInWizardCompleted = async () => {
      vm.$toast.success('予約から卓を作成しました')
      await Promise.all([getHalls(), getTodayReservations()])
    }
    const onCheckInWizardError = message => {
      vm.$toast.error(message || '卓の作成に失敗しました')
    }

    // 予約編集ダイアログ（要コース時の入力導線。日別予約と同じコンポーネント）
    const reservationDialogOpen = ref(false)
    const reservationDialogReservation = ref(null)
    const editReservationFromFloor = reservation => {
      // 予約とopenは同tickで更新する（別tickに分けるとダイアログ内のcomputedが
      // 古いキャッシュのまま開くことがある。ウィザードの同種バグと同根）
      reservationDialogReservation.value = reservation.raw
      reservationDialogOpen.value = true
    }
    const onReservationDialogSaved = async () => {
      vm.$toast.success('予約を保存しました')
      await getTodayReservations()
    }
    // ダイアログの「卓を作成」からもウィザードへ（日別予約と同じ流れ）。
    // reservation はダイアログが保存直後に返す最新のserializer行（一覧の再取得を待つと古い行を掴む）
    const onDialogCheckInRequested = reservation => {
      reservationDialogOpen.value = false
      checkInWizardReservation.value = reservation
      nextTick(() => {
        checkInWizardOpen.value = true
      })
      getTodayReservations()
    }

    const getWorkSchedules = async () => {
      // 「これから出勤するキャスト」用途のため、休み・未定の回答は含めず当営業日のみ取得
      const businessToday = getBusinessTodayStr(new Date(), currentClub.value?.openAt)
      const res = await WorkScheduleApi.getWorkSchedules({
        status: 'approved',
        requestType: 'work',
        startDate: businessToday,
        endDate: businessToday,
      })
      if (res?.data) { workSchedules.value = [...(res.data.work_schedules?.data || res.data.workSchedules?.data || res.data.data || [])] }
    }
    const timecardStatuses = ref([])
    const getTimecardStatuses = async () => {
      const res = await TimecardStatusApi.getTimecardStatuses()
      if (res?.data) {
        timecardStatuses.value = (res.data.timecard_statuses?.data || []).map(d => ({
          id: Number(d.id),
          name: d.attributes.name,
        }))
      }
    }
    const searchCustomers = async (query = '') => {
      if (!query || query.trim().length === 0) return []
      try {
        const res = await CustomerApi.getCustomers({ q: query.trim() })
        if (res?.data?.data) return res.data.data
      } catch (error) { console.error('Failed to search customers:', error) }

      return []
    }
    provide('searchCustomers', searchCustomers)

    // ── Floor layout ──
    // エリア制: currentHallは不要（selectedLayout._hallで個別に参照）
    const ZONE_COLORS = ['#10b981', '#8b5cf6', '#3b82f6', '#f59e0b', '#ef4444', '#ec4899']

    const buildLayouts = hall => {
      const layouts = hall.attributes.tableLayouts || []
      const activeTables = get(hall, 'attributes.tables.data', [])

      return layouts.map(layout => {
        const tn = layout.table_number
        const activeTable = find(activeTables, t => Number(t.attributes.tableNumber) === Number(tn))

        const hallIdx = halls.value.indexOf(hall)

        // 卓の連結: 親卓なら _group、子卓(空卓)なら _childGroup を持たせる
        // eslint-disable-next-line no-use-before-define
        const groups = tableGroups.tableGroupsOf(hall)
        // eslint-disable-next-line no-use-before-define
        const group = tableGroups.findParentGroup(groups, tn)
        // eslint-disable-next-line no-use-before-define
        const childGroup = activeTable ? null : tableGroups.findChildGroup(groups, tn)
        const groupColorIndex = (group || childGroup)?.attributes?.colorIndex

        return {
          ...layout,
          _table: activeTable || null,
          _hall: hall,
          _color: hall.attributes.floorZone?.color || ZONE_COLORS[hallIdx % ZONE_COLORS.length],
          _group: group,
          _childGroup: childGroup,
          _groupColor: (group || childGroup) ? tableGroupColor(groupColorIndex) : '',
        }
      })
    }

    // ── エリア（ゾーン） ──
    // floor_zone は単一オブジェクトだが、追加の領域として sub_zones を持てる。
    // 1ホールを複数の領域に視覚的に分割するためのフロント専用拡張。
    const floorZones = computed(() => {
      const result = []
      halls.value.forEach((hall, hallIdx) => {
        const fz = hall.attributes.floorZone
        if (!fz || typeof fz !== 'object' || Array.isArray(fz)) return

        const color = fz.color || ZONE_COLORS[hallIdx % ZONE_COLORS.length]
        const subs = Array.isArray(fz.sub_zones) ? fz.sub_zones : []
        const hasSubs = subs.length > 0

        // primary
        result.push({
          hallId: hall.id,
          name: hall.attributes.name,
          _color: color,
          x: fz.x,
          y: fz.y,
          w: fz.w,
          h: fz.h,
        })
        // sub-zones
        subs.forEach((sub, subIdx) => {
          result.push({
            hallId: hall.id,
            name: hasSubs ? `${hall.attributes.name} (${subIdx + 2})` : hall.attributes.name,
            _color: color,
            x: sub.x,
            y: sub.y,
            w: sub.w,
            h: sub.h,
          })
        })
      })

      return result
    })

    const zoneStyle = zone => {
      const color = zone._color || zone.color || ZONE_COLORS[0]

      return {
        position: 'absolute',
        left: `${zone.x || 0}px`,
        top: `${zone.y || 0}px`,
        width: `${zone.w || 300}px`,
        height: `${zone.h || 200}px`,
        background: `${color}15`,
        border: `1px solid ${color}50`,
        borderRadius: '8px',
      }
    }

    // ── 全ホールのテーブルをフラットにマージ ──
    const currentLayouts = computed(() => {
      const allLayouts = []
      halls.value.forEach(hall => {
        const hallLayouts = buildLayouts(hall)
        hallLayouts.forEach(layout => {
          allLayouts.push(layout)
        })
      })

      return allLayouts
    })

    // コンパクト表示用: ゾーンごとにテーブルをグループ化
    const compactGroups = computed(() => {
      const groups = []
      let startIdx = 0
      halls.value.forEach((hall, hallIdx) => {
        const hallLayouts = buildLayouts(hall)
          .sort((a, b) => a.table_number - b.table_number)
        const zone = hall.attributes.floorZone
        const color = zone?.color || ZONE_COLORS[hallIdx % ZONE_COLORS.length]
        groups.push({
          name: hall.attributes.name,
          color,
          layouts: hallLayouts,
          layoutStartIdx: startIdx,
        })
        startIdx += hallLayouts.length
      })

      return groups
    })

    const selectedLayout = computed(() => {
      if (selectedLayoutIdx.value === null) return null

      return currentLayouts.value[selectedLayoutIdx.value] || null
    })

    const selectedHall = computed(() => {
      if (!selectedLayout.value) return halls.value[0] || null

      return selectedLayout.value._hall || halls.value[0] || null
    })

    // 選択中ホールの checkoutConfig (税・サービス料・丸めなど) を平坦化
    const hallCheckoutConfig = computed(() => {
      const attrs = selectedHall.value?.attributes?.checkoutConfig?.data?.attributes
        || selectedHall.value?.attributes?.checkoutConfig

      return attrs || {}
    })

    // Drawer用にHalls.vueと互換のtargetTableを用意
    const targetTable = reactive({ hallIdx: undefined, tableIdx: undefined })

    // ── 時間表示用タイマー ──
    const now = ref(Date.now())
    const timerInterval = setInterval(() => { now.value = Date.now() }, 30000)
    onBeforeUnmount(() => {
      clearInterval(timerInterval)
      // HallChannel購読を解除(解除しないと遷移のたびに購読と卓データへの参照が蓄積する)
      subscription.unsubscribe()
      // ワイドモードで離脱する場合にクラスを除去（サイドバー等が復元される）
      document.documentElement.classList.remove('floor-wide-mode')
      // Drawer/dialogを強制的に閉じる
      targetTableActions.show = false
      targetTableActions.create = false
      selectedLayoutIdx.value = null
      // eslint-disable-next-line no-use-before-define
      dropDialog.show = false
      // eslint-disable-next-line no-use-before-define
      removeDialog.show = false
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('dragover', onDocumentDragOver)
      // eslint-disable-next-line no-use-before-define
      document.removeEventListener('drop', onDocumentDrop)
    })

    const formatElapsed = startAt => _formatElapsed(startAt, now.value)
    const formatRemainingSigned = expireAt => _formatRemainingSigned(expireAt, now.value)
    const isExpiringSoon = expireAt => _isExpiringSoon(expireAt, now.value)
    const isOvertime = expireAt => _isOvertime(expireAt, now.value)

    // 残り時間の文字色は卓終了アラート設定（1回目=黄 / 2回目=赤）に連動し、超過は常に赤
    const getRemainingTimeStatus = expireAt => _getRemainingTimeStatus(
      expireAt,
      currentClub.value?.firstFinishNoticeMinutes,
      currentClub.value?.secondFinishNoticeMinutes,
      now.value,
    )

    // ── キャストリスト ──
    // エリア制: 常に全ホール対象
    const getTargetHalls = () => halls.value

    // 当日（営業日）の確定シフトを返す。date を持たない旧データは日付判定をスキップする
    const findTodayShift = userId => {
      const todayStr = getBusinessTodayStr(new Date(), currentClub.value?.openAt)

      return find(workSchedules.value, s => {
        const attrs = s.attributes || s
        if (Number(attrs.userId) !== Number(userId)) return false
        if (attrs.status && attrs.status !== 'approved') return false
        if (attrs.date) return String(attrs.date).slice(0, 10) === todayStr

        return true
      })
    }

    const castList = computed(() => {
      const targetHalls = getTargetHalls()
      if (targetHalls.length === 0) return []
      const activeTablesList = flatMap(targetHalls, h => get(h, 'attributes.tables.data', []))
      const castMap = {}
      // timecardからキャストを取得（非キャストは除外）
      // 出勤中（leaveAt無し）に加え、勤怠ステータス（欠勤・遅刻など）付きも対象に含める。
      timecards.value.forEach(tc => {
        const statusName = getTimecardStatusName(tc)
        // 退勤済み かつ 勤怠ステータス無し のタイムカードは対象外
        if (!isActiveTimecard(tc) && !statusName) return
        const { userId } = tc.attributes
        const user = find(users.value, u => Number(u.id) === Number(userId))
        if (!user) return
        if (!isCast(user)) return
        // 当日（営業日）の確定シフトから出勤予定〜退勤予定を取得
        const ws = findTodayShift(userId)
        const wsAttrs = ws?.attributes || ws
        const existing = castMap[userId]
        // 同一ユーザーで複数タイムカードがある場合、勤怠ステータス付きを優先して保持する
        if (existing && existing._status && !statusName) return
        castMap[userId] = {
          id: userId,
          name: user.attributes?.name || `ID:${userId}`,
          _assignedTables: existing?._assignedTables || [],
          _shiftTime: formatShiftRange(wsAttrs?.startAt, wsAttrs?.endAt),
          _status: statusName,
          _timecardId: tc.id,
          _timecardStatusId: tc.attributes.timecardStatusId || null,
        }
      })
      // cast_user_ids + 指名からキャストの配置先を取得
      activeTablesList.forEach(table => {
        const tableHall = find(halls.value, h => Number(h.id) === Number(table.attributes.hallId))
        const tablePrefix = tableHall?.attributes?.tablePrefix || ''
        const tableSuffix = tableHall?.attributes?.tableSuffix || ''
        const tableName = `${tablePrefix}${table.attributes.tableNumber}${tableSuffix}`
        // フリー
        const castIds = table.attributes.castUserIds || []
        castIds.forEach(uid => {
          if (castMap[uid]) {
            castMap[uid]._assignedTables.push({
              name: tableName,
              tableId: table.id,
              type: 'free',
              since: table.attributes.startAt || null,
            })
          }
        })
        // 指名
        const noms = get(table, 'meta.currentNominations.data', [])
        noms.forEach(nom => {
          const recipients = get(nom, 'attributes.vendibleRecipients.data', [])
          recipients.forEach(r => {
            const uid = get(r, 'attributes.userId')
            if (uid && castMap[uid]) {
              castMap[uid]._assignedTables.push({
                name: `${tableName}(${nom.attributes?.vendibleName || '指名'})`,
                tableId: table.id,
                type: 'nomination',
                vendibleId: nom.attributes?.vendibleId,
                since: nom.attributes?.createdAt || null,
              })
            }
          })
        })
      })

      return Object.values(castMap)
    })

    // 勤怠ステータス付き（欠勤・遅刻など）は配置中/待機から除外し、専用セクションに表示する
    const freeCasts = computed(() => castList.value.filter(c => !c._status && c._assignedTables.length === 0))
    const assignedCasts = computed(() => castList.value.filter(c => !c._status && c._assignedTables.length > 0))
    // 勤怠ステータス付きキャストをステータス名ごとにグルーピング
    const statusCasts = computed(() => castList.value.filter(c => c._status))
    const statusGroups = computed(() => groupCastsByStatus(statusCasts.value))
    // 出勤数（勤怠ステータス無し = 実際に出勤しているキャスト）
    const workingCasts = computed(() => castList.value.filter(c => !c._status))

    // ── 出勤中のキャスト以外スタッフ（showAllRoles ON時のみ）──
    const staffList = computed(() => {
      if (!showAllRoles.value) return []

      return timecards.value
        .filter(tc => isActiveTimecard(tc))
        .map(tc => {
          const { userId } = tc.attributes
          const user = find(users.value, u => Number(u.id) === Number(userId))
          if (!user || isCast(user)) return null
          const ws = findTodayShift(userId)
          const wsAttrs = ws?.attributes || ws

          return {
            id: userId,
            name: user.attributes?.name || `ID:${userId}`,
            role: user.attributes?.role,
            _shiftTime: formatShiftRange(wsAttrs?.startAt, wsAttrs?.endAt),
          }
        })
        .filter(u => u !== null)
    })

    // ── これから出勤するキャスト ──
    const upcomingCasts = computed(() => {
      const workedUserIds = new Set(timecards.value.map(tc => Number(tc.attributes.userId)))
      const todayStr = getBusinessTodayStr(new Date(), currentClub.value?.openAt)

      return workSchedules.value
        .filter(ws => {
          const attrs = ws.attributes || ws
          const uid = Number(attrs.userId)
          if (workedUserIds.has(uid)) return false
          // 確定シフトのみ（希望・却下・取下げは対象外）
          if (attrs.status && attrs.status !== 'approved') return false
          // 当日（営業日）のシフトのみ。時刻なしシフトがあるため date で判定する
          const wsStr = attrs.date ? String(attrs.date).slice(0, 10) : null
          if (wsStr !== todayStr) return false
          if (!showAllRoles.value) {
            const user = find(users.value, u => Number(u.id) === uid)
            if (!isCast(user)) return false
          }

          return true
        })
        .map(ws => {
          const attrs = ws.attributes || ws
          const uid = attrs.userId
          const user = find(users.value, u => Number(u.id) === Number(uid))

          return {
            userId: Number(uid),
            name: user?.attributes?.name || `ID:${uid}`,
            startAt: attrs.startAt ? formatTime(attrs.startAt) : null,
            endAt: attrs.endAt ? formatTime(attrs.endAt) : null,
            // 未出勤アラート: 出勤予定時刻を過ぎても未打刻
            isLate: isPastScheduledStart(attrs.startAt, now.value),
          }
        })
    })

    // ── フロアからの勤怠操作（出勤・勤怠ステータス） ──

    // 出勤時刻の登録ダイアログ（卓作成ウィザードと同じドラムピッカー）。
    // 入口は ①出勤予定の「出勤」ボタン ②遅刻・当欠ステータスからの「出勤にする」
    const checkInTimeDialog = reactive({
      show: false,
      userId: null,
      timecardId: null, // null=新規出勤（create）/ あり=ステータス解除して出勤（update）
      castName: '',
      value: new Date(),
      submitting: false,
    })

    const openCheckInTimeDialog = ({ userId, name, timecardId = null }) => {
      checkInTimeDialog.userId = userId
      checkInTimeDialog.timecardId = timecardId
      checkInTimeDialog.castName = name
      checkInTimeDialog.value = new Date()
      checkInTimeDialog.show = true
    }

    const confirmCheckInTime = async () => {
      checkInTimeDialog.submitting = true
      try {
        const startAt = formatISO(checkInTimeDialog.value)
        if (checkInTimeDialog.timecardId) {
          // 遅刻・当欠→出勤: ステータスを解除して実出勤時刻を登録する
          await TimecardApi.updateTimecard({
            id: checkInTimeDialog.timecardId,
            timecardStatusId: '',
            startAt,
          })
        } else {
          await TimecardApi.createTimecard({
            userId: checkInTimeDialog.userId,
            startAt,
          })
        }
        vm.$toast.success(`${checkInTimeDialog.castName} を出勤にしました`)
        checkInTimeDialog.show = false
        await getTimecards()
      } catch (error) {
        const message = error?.response?.data?.message
        vm.$toast.error(Array.isArray(message) ? message.join('、') : '出勤処理に失敗しました')
      } finally {
        checkInTimeDialog.submitting = false
      }
    }

    const statusDialog = reactive({
      show: false,
      mode: 'create', // create: 出勤予定者にステータス付きでタイムカード作成 / update: 既存タイムカードのステータス変更
      userId: null,
      timecardId: null,
      castName: '',
      selectedStatusId: null,
      currentStatusId: null,
      submitting: false,
    })

    const openStatusDialogForUpcoming = cast => {
      statusDialog.mode = 'create'
      statusDialog.userId = cast.userId
      statusDialog.timecardId = null
      statusDialog.castName = cast.name
      statusDialog.selectedStatusId = null
      statusDialog.currentStatusId = null
      statusDialog.show = true
    }

    const openStatusDialogForCast = cast => {
      statusDialog.mode = 'update'
      statusDialog.userId = cast.id
      statusDialog.timecardId = cast._timecardId
      statusDialog.castName = cast.name
      statusDialog.selectedStatusId = cast._timecardStatusId
      statusDialog.currentStatusId = cast._timecardStatusId
      statusDialog.show = true
    }

    const submitStatusDialog = async () => {
      if (!statusDialog.selectedStatusId) return
      statusDialog.submitting = true
      try {
        if (statusDialog.mode === 'create') {
          await TimecardApi.createTimecard({
            userId: statusDialog.userId,
            startAt: simpleCurrentTime(currentClub.value),
            timecardStatusId: statusDialog.selectedStatusId,
          })
        } else {
          await TimecardApi.updateTimecard({
            id: statusDialog.timecardId,
            timecardStatusId: statusDialog.selectedStatusId,
          })
        }
        vm.$toast.success(`${statusDialog.castName} の勤怠ステータスを設定しました`)
        statusDialog.show = false
        await getTimecards()
      } catch (error) {
        const message = error?.response?.data?.message
        vm.$toast.error(Array.isArray(message) ? message.join('、') : '勤怠ステータスの設定に失敗しました')
      } finally {
        statusDialog.submitting = false
      }
    }

    // ステータスダイアログから「出勤にする」: 時刻入力へ合流
    const switchStatusDialogToCheckIn = () => {
      statusDialog.show = false
      openCheckInTimeDialog({
        userId: statusDialog.userId,
        name: statusDialog.castName,
        timecardId: statusDialog.timecardId,
      })
    }

    const clearCastStatus = async () => {
      statusDialog.submitting = true
      try {
        await TimecardApi.updateTimecard({
          id: statusDialog.timecardId,
          timecardStatusId: '',
        })
        vm.$toast.success(`${statusDialog.castName} の勤怠ステータスを解除しました`)
        statusDialog.show = false
        await getTimecards()
      } catch (error) {
        vm.$toast.error('勤怠ステータスの解除に失敗しました')
      } finally {
        statusDialog.submitting = false
      }
    }

    // ── テーブル残り時間サマリー ──
    const tableTimeSummary = computed(() => {
      const targetHalls = getTargetHalls()
      if (targetHalls.length === 0) return []
      const activeTablesList = flatMap(targetHalls, h => get(h, 'attributes.tables.data', []))

      return activeTablesList
        .map(table => {
          const expireAt = table.meta?.expireAt
          const tableHall = find(halls.value, h => Number(h.id) === Number(table.attributes.hallId))
          const tableLayout = (tableHall?.attributes?.tableLayouts || [])
            .find(l => Number(l.table_number) === Number(table.attributes.tableNumber))
          const tableName = tableLayout?.name
            || `${tableHall?.attributes?.tablePrefix || ''}${table.attributes.tableNumber}${tableHall?.attributes?.tableSuffix || ''}`
          const remaining = formatRemainingSigned(expireAt)
          // eslint-disable-next-line no-use-before-define
          const casts = getTableCasts(table)
          const castNames = casts.map(c => c.name).join(', ')

          const remainingMs = expireAt ? new Date(expireAt).getTime() - now.value : Infinity

          return {
            hallId: table.attributes.hallId,
            tableNumber: table.attributes.tableNumber,
            tableName,
            remaining: remaining || '無制限',
            remainingMs,
            isExpiring: expireAt ? isExpiringSoon(expireAt) : false,
            castNames: castNames || null,
          }
        })
        .sort((a, b) => a.remainingMs - b.remainingMs)
    })

    // ── ドラッグ&ドロップ ──
    const dragCastId = ref(null)

    const onCastDragStart = (e, cast) => {
      dragCastId.value = cast.id
      e.dataTransfer.effectAllowed = 'move'
      // text/plain だと卓外にドロップした瞬間にブラウザがキャスト ID を
      // 検索語／URL と解釈して画面遷移する（特に iPadOS Safari で顕著）。
      // カスタム MIME にしてブラウザのデフォルト解釈を避ける。
      e.dataTransfer.setData('application/x-yonarezi-cast-id', String(cast.id))
    }

    const onCastDragEnd = () => {
      dragCastId.value = null
    }

    // 卓外へのドロップでブラウザ既定動作（URL/検索への遷移）が走らないよう、
    // ドキュメントレベルでも preventDefault する。dragCastId が立っている間だけ作用。
    const onDocumentDragOver = e => {
      if (dragCastId.value) e.preventDefault()
    }
    const onDocumentDrop = e => {
      if (dragCastId.value) {
        e.preventDefault()
        dragCastId.value = null
      }
    }
    onMounted(() => {
      document.addEventListener('dragover', onDocumentDragOver)
      document.addEventListener('drop', onDocumentDrop)
    })

    const onTableDragOver = (e, layout) => {
      if (!dragCastId.value) return
      e.preventDefault()
      if (layout._table && !layout._table.hold) {
        e.currentTarget.style.transform = 'scale(1.03)'
      }
    }

    const onTableDragLeave = e => {
      e.currentTarget.style.transform = 'scale(1)'
    }

    // ── ドロップダイアログ ──
    const dropDialog = reactive({
      show: false,
      castId: null,
      tableId: null,
      tableNumber: null,
      castName: '',
      tableName: '',
    })

    const onTableDrop = (e, layout) => {
      e.preventDefault()
      e.currentTarget.style.transform = 'scale(1)'
      const castId = dragCastId.value
      dragCastId.value = null
      if (!castId || !layout._table || layout._table.hold) return

      const castUser = find(users.value, u => Number(u.id) === Number(castId))
      const hall = layout._hall
      const tableName = layout.name || `${hall?.attributes?.tablePrefix || ''}${layout.table_number}${hall?.attributes?.tableSuffix || ''}`

      // 同じ卓に既にフリー・指名で付いていたらブロック
      // eslint-disable-next-line no-use-before-define
      const tableCasts = getTableCasts(layout._table)
      if (tableCasts.find(c => c.userId === Number(castId))) {
        vm.$toast.warning(`${castUser?.attributes?.name || ''} は既に ${tableName} に配置済みです`)

        return
      }

      dropDialog.castId = castId
      dropDialog.tableId = layout._table.id
      dropDialog.tableNumber = layout.table_number
      dropDialog.castName = castUser?.attributes?.name || ''
      dropDialog.tableName = tableName
      dropDialog.show = true
    }

    // NOTE: Vuetify v-dialog 閉じ後に html/body の overflow ロックが残るケースの対策。
    // ダイアログ閉じアニメーション(約300ms)後にも再ロックが起きうるため、
    // 即時 + 1回遅延の計2回でリセットする (以前の 0/300/600 三連発を簡略化)。
    const resetBodyOverflow = () => {
      const doReset = () => {
        document.documentElement.style.overflow = ''
        document.documentElement.style.position = ''
        document.documentElement.classList.remove('overflow-y-hidden')
        document.body.style.overflow = ''
        document.body.style.position = ''
      }
      doReset()
      setTimeout(doReset, 350)
    }

    const cancelDrop = () => {
      dropDialog.show = false
      resetBodyOverflow()
    }

    const confirmDrop = async (type, nomination = null) => {
      dropDialog.show = false
      const { castId, tableId } = dropDialog

      try {
        if (type === 'free') {
          const allTables = flatMap(halls.value, h => get(h, 'attributes.tables.data', []))
          const activeTable = find(
            allTables,
            t => Number(t.id) === Number(tableId),
          )
          if (!activeTable) return

          const newCastUserIds = buildFreeCastUserIds(activeTable.attributes.castUserIds, castId)
          if (newCastUserIds === null) return

          const res = await TableApi.updateTable({
            tableId,
            params: { castUserIds: newCastUserIds },
          })
          if (res?.data?.data) {
            // eslint-disable-next-line no-use-before-define
            spliceAndBroadcastTable(res.data.data)
            vm.$toast.success(`${dropDialog.castName} → ${dropDialog.tableName}（フリー）`)
          }
        } else {
          if (!nomination) {
            vm.$toast.error('指名メニューが設定されていません')

            return
          }

          const res = await VendingHistoryApi.createVendingHistories({
            tableId,
            vendingHistories: [buildNominationVendingHistoryParams(nomination, castId)],
          })
          if (res?.data?.table?.data) {
            // eslint-disable-next-line no-use-before-define
            spliceAndBroadcastTable(res.data.table.data)
            vm.$toast.success(`${dropDialog.castName} → ${dropDialog.tableName}（${nomination.attributes.name}）`)
          }
        }
      } catch (error) {
        console.error('Cast assignment failed:', error)
        vm.$toast.error('キャストの配置に失敗しました')
      }
      resetBodyOverflow()
    }

    const canvasContentSize = computed(() => {
      let maxX = 0
      let maxY = 0
      currentLayouts.value.forEach(l => {
        maxX = Math.max(maxX, (l.x || 0) + (l.w || 120))
        maxY = Math.max(maxY, (l.y || 0) + (l.h || 100))
      })
      floorZones.value.forEach(z => {
        maxX = Math.max(maxX, (z.x || 0) + (z.w || 300))
        maxY = Math.max(maxY, (z.y || 0) + (z.h || 200))
      })
      // コンテンツが何もない場合のフォールバック
      if (maxX === 0) maxX = CANVAS_WIDTH
      if (maxY === 0) maxY = CANVAS_HEIGHT

      return { w: maxX, h: maxY }
    })

    const fitToContent = () => {
      const wrapper = document.querySelector('.floor-canvas-wrapper')
      if (!wrapper) return
      const { w, h } = canvasContentSize.value
      if (!wrapper.clientWidth || !wrapper.clientHeight) return
      const fitW = wrapper.clientWidth / w
      const fitH = wrapper.clientHeight / h
      const fit = Math.min(fitW, fitH) * 0.95
      zoomLevel.value = Math.max(0.3, Math.min(2, Math.floor(fit * 100) / 100))
      saveZoom()
    }

    const canvasStyle = computed(() => ({
      position: 'relative',
      width: `${canvasContentSize.value.w}px`,
      height: `${canvasContentSize.value.h}px`,
      marginRight: `${canvasContentSize.value.w * (zoomLevel.value - 1)}px`,
      marginBottom: `${canvasContentSize.value.h * (zoomLevel.value - 1)}px`,
    }))

    const tableStyle = layout => {
      const style = {
        position: 'absolute',
        left: `${layout.x}px`,
        top: `${layout.y}px`,
        width: `${layout.w}px`,
        height: `${layout.h}px`,
      }
      // 卓の連結: 枠線は状態色(緑/紫/黄/赤)専用なので、上辺の帯とティントでグループ色を出す
      if (layout._groupColor) {
        style.borderTop = `5px solid ${layout._groupColor}`
        if (layout._childGroup) style.backgroundColor = `${layout._groupColor}29`
      }

      return style
    }

    // ── ヘルパー ──
    const getTableStatusClass = layout => {
      if (!layout._table || layout._table.hold) return 'floor-table--empty'
      const { expireAt } = layout._table.meta || {}
      // 卓終了アラート設定（Club の 1回目=黄 / 2回目=赤）に沿って色分けする。
      // 設定が未設定（0/null）の場合は終了系の色を出さず、通常色（緑/紫）にフォールバックする。
      const finishNoticeStatus = getFinishNoticeStatus(
        expireAt,
        currentClub.value?.firstFinishNoticeMinutes,
        currentClub.value?.secondFinishNoticeMinutes,
        now.value,
      )
      if (finishNoticeStatus === 'expired') return 'floor-table--expired'
      if (finishNoticeStatus === 'ending') return 'floor-table--ending'

      const noms = get(layout._table, 'meta.currentNominations.data', [])
      if (noms.length > 0) return 'floor-table--designated'

      return 'floor-table--occupied'
    }

    const getTableCasts = table => _getTableCasts(table, users.value)
    const getTableCastsSplit = table => splitCastsForDisplay(getTableCasts(table), 2)
    // コンパクトカードは幅が狭くテキスト長も短いためより多く並べられる。
    // カード縦長肥大を防ぐ目的で max 3 に制限、超過は「+N名」バッジで表示。
    const getCompactCastsSplit = table => splitCastsForDisplay(getTableCasts(table), 3)

    // ── キャスト種別カラー ──
    // フリー/指名の種別に応じて、卓上のキャストチップの縁色を決める。
    // キャスト投下ダイアログ (フリー=Teal / 指名=商品カラー) と色を揃え、
    // 画面全体で「この色はフリー / この色は本指名 / ...」と一貫して読み取れるようにする。
    //
    // フリー卓を示す色。指名で選べる 5 色 (grey/green/blue/orange/red) と
    // 被らず、紫プライマリのアプリテーマに合う Teal を採用。
    const FREE_CAST_COLOR = '#14b8a6'

    // DB の vendible.color には Vuetify のセマンティック名 (primary/info/success
    // 等) や Material 色名 (purple/blue 等) が保存されている。CSS background に
    // 直接使えないため、Vuetify テーマと Material パレットから実 hex に解決する。
    const resolveVendibleColor = raw => {
      if (!raw || typeof raw !== 'string') return null
      if (raw.startsWith('#')) return raw
      const themeColors = vm.$vuetify?.theme?.themes?.[vm.$vuetify.theme.dark ? 'dark' : 'light']
      if (themeColors && themeColors[raw]) return themeColors[raw]
      if (materialColors[raw]?.base) return materialColors[raw].base

      return null
    }
    const getCastColor = cast => {
      if (!cast) return '#64748b'
      if (cast.type === 'free') return FREE_CAST_COLOR
      if (cast.type === 'nomination') {
        const nom = nominations.value.find(n => Number(n.id) === Number(cast.vendibleId))

        return resolveVendibleColor(nom?.attributes?.color) || '#64748b'
      }

      return '#64748b'
    }

    const removeDialog = reactive({
      show: false,
      table: null,
      casts: [],
      selectedKeys: [],
      processing: false,
    })

    const castKey = cast => `${cast.type}:${cast.userId}`

    const findTableById = tableId => {
      // eslint-disable-next-line no-restricted-syntax
      for (const hall of halls.value) {
        const tables = get(hall, 'attributes.tables.data', [])
        const t = tables.find(tbl => Number(tbl.id) === Number(tableId))
        if (t) return t
      }

      return null
    }

    const removeCast = (table, cast) => {
      removeDialog.table = table
      removeDialog.casts = getTableCasts(table)
      removeDialog.selectedKeys = cast ? [castKey(cast)] : []
      removeDialog.processing = false
      removeDialog.show = true
    }

    // サイドパネルの「キャストカード → 配置中の卓バッジ」クリックから解除ダイアログを開く
    const openRemoveFromCastCard = (castUserId, at) => {
      const table = findTableById(at.tableId)
      if (!table) return
      const casts = getTableCasts(table)
      const target = casts.find(
        c => Number(c.userId) === Number(castUserId) && c.type === at.type,
      )
      removeCast(table, target || null)
    }

    // 「+N名」オーバーフロー / キャスト集合バッジのクリックから、
    // その卓のキャストを一覧表示してユーザーに選ばせる（プリセレクト無し）。
    const openRemoveForTable = table => {
      removeCast(table, null)
    }

    const confirmRemoveCast = async () => {
      const { table, casts, selectedKeys } = removeDialog
      if (!table || !selectedKeys.length) return

      const targets = casts.filter(c => selectedKeys.includes(castKey(c)))
      if (!targets.length) return

      removeDialog.processing = true

      try {
        const freeTargets = targets.filter(c => c.type === 'free')
        const nominationTargets = targets.filter(c => c.type === 'nomination')
        let latestTableData = null

        if (freeTargets.length > 0) {
          const removeIds = freeTargets.map(c => Number(c.userId))
          const currentIds = table.attributes.castUserIds || []
          const newIds = currentIds.filter(id => !removeIds.includes(Number(id)))
          const res = await TableApi.updateTable({
            tableId: table.id,
            params: { castUserIds: newIds },
          })
          if (res?.data?.data) latestTableData = res.data.data
        }

        if (nominationTargets.length > 0) {
          // 同じ vending_history にまとめる。その vh に含まれる recipient を
          // すべて削除する場合は vh 自体を destroy、そうでなければ該当 recipient
          // のみ destroy する。
          const byVh = new Map()
          nominationTargets.forEach(c => {
            const existing = byVh.get(c.vendingHistoryId) || {
              recipientIds: [],
              totalRecipientCount: c.recipientCount || 1,
            }
            existing.recipientIds.push(c.vendibleRecipientId)
            byVh.set(c.vendingHistoryId, existing)
          })

          const vendingHistories = Array.from(byVh.entries()).map(([vhId, info]) => {
            const destroyWhole = info.recipientIds.length >= info.totalRecipientCount
            if (destroyWhole) return { id: vhId, destroy: true }

            return {
              id: vhId,
              vendibleRecipientsAttributes: info.recipientIds.map(id => ({ id, destroy: true })),
            }
          })

          const res = await VendingHistoryApi.updateVendingHistories({
            tableId: table.id,
            vendingHistories,
          })
          if (res?.data?.table?.data) latestTableData = res.data.table.data
        }

        if (latestTableData) {
          // eslint-disable-next-line no-use-before-define
          spliceAndBroadcastTable(latestTableData)
        }

        vm.$toast.success(
          targets.length === 1
            ? `${targets[0].name} を解除しました`
            : `${targets.length}名を解除しました`,
        )
        removeDialog.show = false
      } catch (error) {
        console.error('Cast removal failed:', error)
        vm.$toast.error('キャストの解除に失敗しました')
      } finally {
        removeDialog.processing = false
      }
    }

    const toggleAllRemoveSelection = () => {
      if (removeDialog.selectedKeys.length === removeDialog.casts.length) {
        removeDialog.selectedKeys = []
      } else {
        removeDialog.selectedKeys = removeDialog.casts.map(castKey)
      }
    }

    // ── Table actions ──
    const onTableClick = layoutIdx => {
      if (dragCastId.value) return
      const layout = currentLayouts.value[layoutIdx]
      if (!layout) return

      selectedLayoutIdx.value = layoutIdx
      const hallIdx = halls.value.findIndex(h => Number(h.id) === Number(layout._hall?.id))
      const tableIdx = layout.table_number - 1

      targetTable.hallIdx = hallIdx
      targetTable.tableIdx = tableIdx

      if (layout._table && layout._table.hold === undefined) {
        targetTableActions.show = true
        subscription.occupyPlace(hallIdx, tableIdx, null)
      } else if (!layout._table && layout._childGroup) {
        // 子卓(連結中の空卓)は入店受付を開かず、解除の確認だけ出す
        selectedLayoutIdx.value = null
        targetTable.hallIdx = undefined
        targetTable.tableIdx = undefined
        unlinkDialog.hallIdx = hallIdx
        unlinkDialog.tableNumber = layout.table_number
        unlinkDialog.tableName = layout.name || `${layout._hall.attributes.tablePrefix}${layout.table_number}${layout._hall.attributes.tableSuffix}`
        unlinkDialog.parentName = childGroupParentName(layout)
        unlinkDialog.show = true
      } else if (!layout._table) {
        targetTableActions.create = true
        subscription.occupyPlace(hallIdx, tableIdx, actionType.new)
      }
    }

    const closeDrawer = ({ strategy }) => {
      const { hallIdx, tableIdx } = targetTable
      const { create, created } = targetTableActions

      targetTable.hallIdx = undefined
      targetTable.tableIdx = undefined
      selectedLayoutIdx.value = null
      targetTableActions.create = false
      targetTableActions.created = false
      targetTableActions.show = false
      targetTableActions.updated = false

      const tableNumber = tableIdx + 1
      if (create && !created) subscription.deleteOccupyPlace({ hallIdx, tableNumber })
      resetBodyOverflow()
    }

    // ── QRコード印刷 ──
    // Layout が起動時に確立した receipt printer を共有することで、
    // 同一プリンタに対する並走接続を回避する（応答ルーティングの混乱防止）。
    const receiptPrinter = inject('printer', ref(null))
    const qrPrintOptions = ref({
      ipAddress: '', qrCodeUrl: '', hallName: '', tableName: '', startAt: '',
    })
    const qrPrintOptionsComputed = computed(() => ({ ...qrPrintOptions.value, printer: receiptPrinter.value }))
    const { send: sendQrSlipPrint, onReceive: onReceiveQrPrint } = useQrCodeSlipPrint(qrPrintOptionsComputed)
    onReceiveQrPrint.value = wrapPrinterReceive(null, {
      kind: 'receipt',
      slipKind: 'qr-code',
      ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
      clubId: currentClub.value?.id,
      printerName: 'レシートプリンタ',
      toast: vm.$toast,
    })
    const { isSdpEnabled, requestPrint: requestSdpPrint } = useSdpSlipPrint({ toast: vm.$toast })

    const printQrCodeForTable = async (tableData, hallData) => {
      if (!canExecutePrintOnThisDevice()) return
      try {
        const tableId = tableData.id
        const res = await TableApi.getSelfOrderQr(tableId)
        const data = res.data.data || res.data
        if (data.security_level !== 'secure') return
        const tablePrefix = hallData.attributes.tablePrefix || ''
        const tableSuffix = hallData.attributes.tableSuffix || ''
        const tableName = `${tablePrefix}${tableData.attributes.tableNumber}${tableSuffix}`
        let formattedStartAt = ''
        if (tableData.attributes.startAt) {
          try { formattedStartAt = format(parseISO(tableData.attributes.startAt), 'yyyy/MM/dd HH:mm') } catch { formattedStartAt = tableData.attributes.startAt }
        }
        qrPrintOptions.value = {
          ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
          qrCodeUrl: data.url,
          hallName: hallData.attributes.name || '',
          tableName,
          startAt: formattedStartAt,
        }
        // SDP有効店舗はサーバー経由で印刷（QRはePOS XMLの<symbol>でネイティブ描画）
        if (isSdpEnabled()) {
          await requestSdpPrint({ ticketType: 'qr_code', tableId: tableData.id, inputs: { qr_code_url: data.url } })
        } else {
          await sendQrSlipPrint()
        }
        vm.$toast.success('QRコードを印刷しました')
      } catch (error) {
        console.error('QRコード自動印刷エラー:', error)
        reportPrinterError(error, {
          kind: 'receipt',
          phase: 'print',
          slipKind: 'qr-code',
          ipAddress: removePortFromIpAddress(currentClub.value?.printIpAddress || ''),
          clubId: currentClub.value?.id,
        })
        vm.$toast.error('QRコードの印刷に失敗しました')
      }
    }

    const addTable = tableData => {
      const targetHall = find(halls.value, hall => Number(hall.id) === Number(get(tableData, 'attributes.hallId')))
      if (!targetHall?.attributes) return
      const hallIdx = halls.value.findIndex(h => Number(h.id) === Number(targetHall.id))
      targetHall.attributes.tables.data.push(tableData)
      targetTableActions.created = true
      subscription.updateTable({ hallIdx, tableData })
      Promise.all([getClub(), fetchPlusMinusStats(), fetchOperationRateStats()])
      if (tableData.attributes.selfOrderStatus === 'open') {
        printQrCodeForTable(tableData, targetHall)
      }
    }

    const spliceAndBroadcastTable = tableData => {
      const hallIdx = halls.value.findIndex(hall => Number(hall.id) === Number(get(tableData, 'attributes.hallId')))
      if (hallIdx === -1) return null
      const targetHall = halls.value[hallIdx]
      const tableIdx = findIndex(
        targetHall.attributes.tables.data,
        table => Number(table.attributes.tableNumber) === Number(tableData.attributes.tableNumber),
      )
      if (tableIdx === -1) return null
      targetHall.attributes.tables.data.splice(tableIdx, 1, tableData)
      subscription.updateTable({ hallIdx, tableData })

      return { hallIdx, tableIdx }
    }

    const updateTable = tableData => {
      if (!spliceAndBroadcastTable(tableData)) return
      Promise.all([getClub(), fetchPlusMinusStats(), fetchOperationRateStats()])
    }

    const checkoutTable = async (hallIdxArg, receiptData) => {
      // ALLビュー時にhallIdxが-1の場合、テーブルIDから逆引き
      let hallIdx = hallIdxArg
      if (hallIdx < 0) {
        hallIdx = halls.value.findIndex(h => findIndex(
          h.attributes.tables.data,
          t => +t.id === +receiptData.attributes.tableId,
        ) >= 0)
      }
      if (hallIdx < 0) return
      const tableIdx = findIndex(
        halls.value[hallIdx].attributes.tables.data,
        table => +table.id === +receiptData.attributes.tableId,
      )
      if (tableIdx < 0) return
      const table = halls.value[hallIdx].attributes.tables.data[tableIdx]
      const tableId = +table.id
      // 会計時にcast_user_idsをクリア
      if (table.attributes.castUserIds && table.attributes.castUserIds.length > 0) {
        TableApi.updateTable({ tableId, params: { castUserIds: [] } }).catch(() => {})
      }
      halls.value[hallIdx].attributes.tables.data.splice(tableIdx, 1)
      vm.$toast.success(`${receiptData.meta.tableName}の会計をしました`)
      subscription.deleteOccupyPlace({ hallIdx, tableNumber: receiptData.meta.tableNumber })
      receipt.value = receiptData

      const hallId = +halls.value[hallIdx].id
      if (tableFinishNoticeColors.value[hallId] && tableFinishNoticeColors.value[hallId][tableId]) {
        tableFinishNoticeColors.value = {
          ...tableFinishNoticeColors.value,
          [hallId]: { ...tableFinishNoticeColors.value[hallId], [tableId]: null },
        }
      }
      Promise.all([getClub(), fetchPlusMinusStats(), fetchOperationRateStats()])
    }

    const moveTable = async (currentHallId, tableId, { hallId, tableNumber, callback }) => {
      try {
        const res = await TableApi.moveTable(tableId, { hallId, tableNumber })
        if (res?.data?.data) {
          const { data: tableData } = res.data
          const updatedHalls = [...halls.value]
          const hallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(currentHallId))
          if (hallIdx < 0) return
          const tIdx = findIndex(updatedHalls[hallIdx].attributes.tables.data, table => Number(table.id) === Number(tableData.id))
          if (tIdx < 0) return
          updatedHalls[hallIdx].attributes.tables.data.splice(tIdx, 1)
          const { hallId: newHallId } = tableData.attributes
          const newHallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(newHallId))
          if (newHallIdx < 0) return
          updatedHalls[newHallIdx].attributes.tables.data.push(tableData)
          if (tableFinishNoticeColors.value[currentHallId]) {
            delete tableFinishNoticeColors.value[currentHallId][tableId]
          }
          halls.value = updatedHalls
          subscription.removeTable({ hallIdx, tableId })
          subscription.addTable({ hallIdx: newHallIdx, tableData })
          vm.$toast.success('卓を移動しました')
        }
      } catch (e) {
        console.error(e)
        vm.$toast.error('卓の移動に失敗しました')
      } finally { callback() }
    }

    const destroyTable = async (currentHallId, tableId) => {
      try {
        const res = await TableApi.deleteTable(tableId)
        if (res?.data) {
          const updatedHalls = [...halls.value]
          const hallIdx = findIndex(updatedHalls, hall => Number(hall.id) === Number(currentHallId))
          if (hallIdx < 0) return
          const tIdx = findIndex(updatedHalls[hallIdx].attributes.tables.data, table => Number(table.id) === Number(tableId))
          if (tIdx < 0) return
          updatedHalls[hallIdx].attributes.tables.data.splice(tIdx, 1)
          halls.value = updatedHalls
          if (tableFinishNoticeColors.value[currentHallId]) {
            delete tableFinishNoticeColors.value[currentHallId]
          }
          subscription.removeTable({ hallIdx, tableId })
          vm.$toast.success('卓を削除しました')
        }
      } catch (e) {
        console.error(e)
        vm.$toast.error('卓の削除に失敗しました')
      }
    }

    // ── 未精算合計の同期 ──
    const activeTables = computed(() => flatMap(halls.value, hall => hall.attributes.tables.data))
    const todaySalesUnfixedTotal = computed(() => sumBy(activeTables.value, o => Number(o.meta.total)))
    watch(todaySalesUnfixedTotal, newVal => {
      currentClubMeta.todaySalesUnfixed = newVal
    }, { immediate: true })

    // SelfOrderChannel連携
    const latestTableUpdate = computed(() => vm.$store.state.selfOrder?.latestTableUpdate)
    const refreshTableById = async tableId => {
      try {
        const res = await TableApi.getTable(tableId)
        if (res?.data?.data) {
          const tableData = res.data.data
          const targetHall = find(halls.value, hall => Number(hall.id) === Number(tableData.attributes.hallId))
          if (!targetHall?.attributes) return
          const tIdx = findIndex(targetHall.attributes.tables.data, table => Number(table.id) === Number(tableId))
          if (tIdx >= 0) {
            targetHall.attributes.tables.data.splice(tIdx, 1, tableData)
            vm.$toast.info(`${tableData.meta.tableName || '卓'}のデータが更新されました`)
          }
        }
      } catch (error) { console.error('Failed to refresh table:', error) }
    }
    watch(latestTableUpdate, newUpdate => {
      if (newUpdate?.tableId) refreshTableById(newUpdate.tableId)
    }, { deep: true })

    const getHallColorCounts = hallId => {
      return computed(() => {
        const colors = tableFinishNoticeColors.value[+hallId] || {}
        let red = 0; let yellow = 0
        Object.values(colors).forEach(color => {
          if (color === 'red') red += 1
          else if (color === 'yellow') yellow += 1
        })

        return { red, yellow }
      })
    }

    initWith([
      getHalls(),
      getUsers(),
      getVendibles(),
      getReferrals(),
      getCustomerTags(),
      getVendingContributionTriggers(),
      getTimecards(),
      getWorkSchedules(),
      getTimecardStatuses(),
      getTodayReservations(),
    ]).then(() => {
      // フロアレイアウト未設計でも自動遷移はせず、画面上で empty state を表示する
      // （ユーザーが何が起きたか分かるように）
      if (!isFloorLayoutConfigured(halls.value)) return

      // 全ホールの卓データを同期
      halls.value.forEach((_, idx) => syncTables(idx))
    })

    const isLayoutConfigured = computed(() => isFloorLayoutConfigured(halls.value))

    const goToFloorLayoutSettings = () => {
      vm.$router.push({
        name: 'waiter-settings-floor',
        params: { club_slug: vm.$route.params.club_slug },
      })
    }

    // ── サイドパネルのタブ / キャスト絞り込み（2026-08-03 タブ化） ──
    const sideTab = ref('casts')
    const castFilter = ref('all')

    const castFilterOptions = computed(() => [
      { value: 'all', label: 'すべて', count: workingCasts.value.length },
      { value: 'assigned', label: '配置中', count: assignedCasts.value.length },
      { value: 'free', label: '待機', count: freeCasts.value.length },
      { value: 'upcoming', label: '出勤予定', count: upcomingCasts.value.length },
    ])

    // 未出勤（出勤予定時刻超過）の人数。キャストタブの警告バッジに使う
    const lateCount = computed(() => upcomingCasts.value.filter(c => c.isLate).length)

    return {
      sideTab,
      castFilter,
      castFilterOptions,
      lateCount,
      isLoading,
      halls,
      // TableCreateWizard に props で渡すため return が必須。
      // return 漏れで props が空になると売上計上トリガが効かなくなる（Halls.vue は return 済み）
      users,
      vendingContributionTriggers,
      useTableCreateWizard,
      hallCheckoutConfig,
      normalizeForWizard,
      isLayoutConfigured,
      goToFloorLayoutSettings,
      wideMode,
      compactMode,
      showAllRoles,
      toggleWideMode,
      zoomLevel,
      zoomIn,
      zoomOut,
      fitToContent,
      floorZones,
      zoneStyle,
      currentLayouts,
      compactGroups,
      selectedLayout,
      selectedHall,
      targetTable,
      targetTableActions,
      actionType,
      receipt,
      colorCodes,
      today,
      canvasStyle,
      tableStyle,
      getHallColorCounts,
      formatElapsed,
      formatRemainingSigned,
      formatTime,
      formatTimeRange,
      isOvertime,
      getRemainingTimeStatus,
      getTableStatusClass,
      isNarrowLayout,
      getTableCasts,
      hasCautionCustomer,
      getTableCastsSplit,
      getCompactCastsSplit,
      getCastColor,
      removeCast,
      removeDialog,
      confirmRemoveCast,
      toggleAllRemoveSelection,
      openRemoveFromCastCard,
      openRemoveForTable,
      castList,
      freeCasts,
      assignedCasts,
      statusGroups,
      workingCasts,
      staffList,
      upcomingCasts,
      tableTimeSummary,
      todayReservations,
      courses,
      expandedReservationId,
      toggleReservationDetail,
      checkInFromFloor,
      checkInWizardOpen,
      checkInWizardReservation,
      floorCasts,
      wizardHalls,
      todayReservationRows,
      onCheckInWizardCompleted,
      onCheckInWizardError,
      reservationDialogOpen,
      reservationDialogReservation,
      editReservationFromFloor,
      onReservationDialogSaved,
      onDialogCheckInRequested,
      customerTags,
      referrals,
      timecardStatuses,
      checkInTimeDialog,
      openCheckInTimeDialog,
      confirmCheckInTime,
      switchStatusDialogToCheckIn,
      statusDialog,
      openStatusDialogForUpcoming,
      openStatusDialogForCast,
      submitStatusDialog,
      clearCastStatus,
      dragCastId,
      onCastDragStart,
      onCastDragEnd,
      onTableDragOver,
      onTableDragLeave,
      onTableDrop,
      dropDialog,
      cancelDrop,
      confirmDrop,
      nominations,
      onTableClick,
      // 卓の連結
      tableGroupDialog: tableGroups.tableGroupDialog,
      onTableGroupSaved: tableGroups.onTableGroupSaved,
      onTableGroupReleased: tableGroups.onTableGroupReleased,
      childGroupParentName,
      openTableGroupFromFloor,
      compactCardStyle,
      unlinkDialog,
      confirmUnlink,
      closeDrawer,
      addTable,
      updateTable,
      spliceAndBroadcastTable,
      checkoutTable,
      moveTable,
      destroyTable,
      icons: {
        mdiAlert,
        mdiLinkVariant,
        mdiLinkVariantOff,
        mdiChevronDown,
        mdiSofaSingle,
      },
    }
  },
}
</script>

<style scoped>
.floor-caution-icon {
  color: #ef5350 !important;
}

.floor-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.floor-header__title {
  font-size: 16px;
  font-weight: 700;
  flex: 1;
}

.floor-header__wide-btn {
  flex-shrink: 0;
}

.floor-header__zoom {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.floor-header__zoom-label {
  font-size: 13px;
  font-weight: 600;
  min-width: 42px;
  text-align: center;
}

.floor-main {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.floor-content {
  flex: 1;
  min-width: 0;
}

.floor-canvas-wrapper {
  flex: 1;
  min-width: 0;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: #1a1a2e;
  height: calc(100vh - 150px);
}

.floor-canvas-wrapper--wide {
  height: calc(100vh - 60px);
}

.floor-canvas {
  background-image:
    radial-gradient(circle, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

.floor-hall-label {
  position: absolute;
  left: 0;
  right: 0;
  height: 24px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 23, 42, 0.8);
  z-index: 1;
}

.floor-zone {
  pointer-events: none;
  z-index: 0;
}

.floor-zone__label {
  position: absolute;
  top: 4px;
  left: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #e2e8f0;
}

.floor-table {
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 8px;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* 卓の連結 */
.floor-table--child {
  opacity: 1;
}

.floor-table__group-chip {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 5px 2px 4px;
  border-radius: 8px;
  color: #fff;
  border: 0;
  cursor: pointer;
}

.floor-table__group-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 9px;
  border: 0;
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
  cursor: pointer;
  opacity: 0.6;
}

.floor-table__group-btn:hover {
  opacity: 1;
}

.floor-table__child-label {
  font-size: 11px;
  font-weight: 600;
  color: #cbd5e1;
}

.theme--light .floor-table__child-label {
  color: rgba(0, 0, 0, 0.7);
}

/* コンパクトビュー */
.compact-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.compact-zone {
  border: 1px solid;
  border-radius: 8px;
  padding: 8px;
}

.compact-zone__label {
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}

.compact-zone__grid {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.compact-card {
  width: 90px;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* 予約由来の卓のピル（紫=予約で統一） */
.table-rsv-pill {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  line-height: 1.5;
  color: #c084fc;
  background: rgba(145, 85, 253, 0.16);
  border: 1px solid rgba(145, 85, 253, 0.55);
  border-radius: 8px;
  padding: 0 6px;
  vertical-align: 1px;
}

.compact-card__name {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.compact-card__time {
  font-size: 14px;
  font-weight: 700;
  color: #6ee7b7;
  font-variant-numeric: tabular-nums;
  /* 「超過 −15分」が1文字ずつ縦に折り返されないようにする */
  white-space: nowrap;
}

/* 残り時間の文字色（卓終了アラート設定に連動。超過は常に赤） */
.compact-card__time--ending {
  color: #fbbf24;
}

.compact-card__time--expired {
  color: #ef4444;
}

.compact-card__overtime-label {
  display: inline-block;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 3px;
  margin-right: 2px;
  border: 1px solid currentColor;
  border-radius: 6px;
  vertical-align: 1px;
}

.compact-card__meta {
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 6px;
}

.compact-card__start {
  font-size: 9px;
  color: #94a3b8;
  margin-right: 3px;
}

.compact-card__range {
  font-size: 9px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1.2;
}

.compact-card__count {
  font-size: 9px;
  color: #94a3b8;
}

.compact-card__total {
  font-size: 10px;
  font-weight: 700;
  color: #e2e8f0;
  font-variant-numeric: tabular-nums;
}

/* シングルチャージ適用卓のバッジ(合計金額の横) */
.table-charge-badge {
  display: inline-block;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 5px;
  margin-left: 4px;
  border: 1px solid rgba(127, 127, 127, 0.6);
  border-radius: 8px;
  vertical-align: 1px;
  white-space: nowrap;
}

.compact-card__casts {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 4px;
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 4px;
}

.compact-card__casts:hover,
.compact-card__casts:focus-visible {
  background: rgba(255, 255, 255, 0.04);
  outline: none;
}

.compact-card__cast {
  font-size: 10px;
  font-weight: 600;
  color: #e2e8f0;
  background: rgba(255, 255, 255, 0.08);
  border-left: 3px solid #64748b;
  border-radius: 3px;
  padding: 1px 4px;
  text-align: left;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.compact-card__cast-overflow {
  font-size: 9px;
  font-weight: 700;
  color: #cbd5e1;
  background: rgba(148, 163, 184, 0.2);
  border-radius: 3px;
  padding: 1px 4px;
  text-align: center;
  line-height: 1.3;
}

.compact-card__hold {
  font-size: 10px;
  color: #475569;
}

.compact-card__empty {
  font-size: 10px;
  color: #475569;
}

/* 卓の連結(コンパクト表示) */
.compact-card__child {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 600;
}

.compact-card__group-chip {
  display: inline-block;
  vertical-align: middle;
  margin-left: 3px;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 7px;
  color: #fff;
  border: 0;
  cursor: pointer;
}

.compact-card__group-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 8px;
  border: 0;
  background: rgba(148, 163, 184, 0.2);
  color: #94a3b8;
  cursor: pointer;
  opacity: 0.6;
}

.compact-card__group-btn:hover {
  opacity: 1;
}

.floor-table:hover {
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.15);
}

.floor-table--drop-target {
  border-style: dashed !important;
  border-color: #8b5cf6 !important;
}

/* 状態別カラー（枠線のみ） */
.floor-table--occupied,
.floor-table--designated,
.floor-table--ending,
.floor-table--expired {
  background: rgba(15, 23, 42, 0.6);
}

.floor-table--occupied {
  border: 2px solid rgba(16, 185, 129, 0.85);
}

.floor-table--designated {
  border: 2px solid rgba(139, 92, 246, 0.85);
}

.floor-table--ending {
  border: 2px solid rgba(245, 158, 11, 0.9);
  position: relative;
}

/*
 * box-shadowの値そのものをアニメーションさせるとフレームごとに再ペイントが発生するため、
 * 影は疑似要素に固定描画し、opacityのみをアニメーションさせる（コンポジタのみの処理にする）。
 * overflow: hiddenな.floor-table配下では外側に伸びるbox-shadowがクリップされるため、
 * inset指定でカード内側に描画する。
 */
.floor-table--ending::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: inset 0 0 18px rgba(245, 158, 11, 0.5);
  opacity: 0.4;
  animation: pulseYellowGlow 1.5s infinite;
  pointer-events: none;
}

.floor-table--expired {
  border: 2px solid rgba(239, 68, 68, 0.9);
  animation: pulseRed 1.5s infinite;
}

.floor-table--empty {
  background: rgba(30, 41, 59, 0.4);
  border: 2px dashed rgba(100, 116, 139, 0.8);
  opacity: 0.7;
}

.floor-table--empty:hover {
  opacity: 0.8;
}

.floor-table:has(.floor-table__hold) {
  background: #1e293b;
  border: 2px dashed #475569;
}

@keyframes pulseRed {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes pulseYellowGlow {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}

.floor-table__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
  line-height: 1.2;
}

.floor-table__name {
  font-weight: 700;
  font-size: 13px;
  color: #fff;
}

.floor-table__guest-badge {
  font-size: 10px;
  padding: 1px 6px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  font-weight: 600;
}

.floor-table__time {
  font-size: 18px;
  font-weight: 700;
  color: #6ee7b7;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  /* 「超過 −15分」が1文字ずつ縦に折り返されないようにする */
  white-space: nowrap;
}

/* 卓カードはレイアウト設定の固定寸法。行同士が縮んで重ならないよう flex-shrink を切り、
   収まらない分はカードの overflow: hidden で切る */
.floor-table__header,
.floor-table__time,
.floor-table__info,
.floor-table__casts {
  flex-shrink: 0;
}

/* 小さく作られた卓（140x110未満）: 残り時間 + 開始〜終了 + 合計 + キャストが縦に収まるよう一段小さく */
.floor-table--narrow .floor-table__name {
  font-size: 12px;
}

.floor-table--narrow .floor-table__guest-badge {
  font-size: 9px;
  padding: 0 5px;
}

.floor-table--narrow .floor-table__time {
  font-size: 15px;
}

.floor-table--narrow .floor-table__overtime-label {
  font-size: 9px;
  padding: 1px 4px;
  vertical-align: 1px;
}

.floor-table--narrow .floor-table__info {
  column-gap: 4px;
  line-height: 1.1;
}

.floor-table--narrow .floor-table__sub {
  font-size: 9px;
}

.floor-table--narrow .floor-table__total {
  font-size: 11px;
}

.floor-table--narrow .floor-table__cast-chip {
  font-size: 9px;
  padding: 1px 3px 1px 5px;
}

/* 残り時間の文字色（卓終了アラート設定に連動。超過は常に赤） */
.floor-table__time--ending {
  color: #fbbf24;
}

.floor-table__time--expired {
  color: #ef4444;
}

.floor-table__overtime-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  padding: 2px 5px;
  margin-right: 2px;
  border: 1px solid currentColor;
  border-radius: 8px;
  vertical-align: 2px;
}

/* 開始〜終了予定時刻 + 合計金額。幅があれば1行、狭い卓カードでは折り返す */
.floor-table__info {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: baseline;
  column-gap: 6px;
  /* .v-application の line-height: 1.5 を継承すると固定寸法の卓カードで縦に溢れるため詰める */
  line-height: 1.2;
}

.floor-table__sub {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.floor-table__start {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin-right: 4px;
}

.floor-table__total {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}

.floor-table__casts {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.floor-table__cast-chip {
  font-size: 10px;
  line-height: 1.2;
  padding: 2px 3px 2px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.floor-table__cast-chip--popover {
  font-size: 12px;
  padding: 4px 6px 4px 8px;
}

.floor-table__cast-overflow {
  font-size: 11px;
  font-weight: 700;
  color: #e2e8f0;
  background: rgba(148, 163, 184, 0.2);
  border: none;
  border-radius: 4px;
  padding: 0 8px;
  min-width: 40px;
  min-height: 24px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.floor-table__cast-overflow:hover {
  background: rgba(148, 163, 184, 0.35);
}

.floor-table__cast-popover {
  background: #1a1a2e;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 280px;
}

.floor-table__cast-type {
  font-size: 8px;
  font-weight: 700;
  opacity: 0.6;
}

.floor-table__cast-remove {
  font-size: 14px;
  line-height: 1;
  color: #ef4444;
  opacity: 0.85;
  cursor: pointer;
  background: rgba(239, 68, 68, 0.15);
  border: none;
  border-radius: 50%;
  padding: 0;
  margin-left: 4px;
  width: 22px;
  height: 22px;
  min-width: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
}

.floor-table__cast-remove:hover {
  opacity: 1;
  background: rgba(239, 68, 68, 0.3);
}

.floor-table__tags {
  display: flex;
  gap: 3px;
  flex-wrap: wrap;
  margin-top: 2px;
}

.floor-table__tag {
  font-size: 8px;
  padding: 0 3px;
  border-radius: 3px;
  background: rgba(59, 130, 246, 0.2);
  color: #93c5fd;
}

.floor-table__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 4px;
}

.floor-table__empty-name {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 600;
}

/* 右サイドパネル */
.side-panel {
  width: 200px;
  flex-shrink: 0;
  max-height: calc(100vh - 150px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 0;
}

.side-panel__section {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: #1a1a2e;
  padding: 10px;
}

.side-panel__section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.side-panel__section-title {
  font-weight: 700;
  font-size: 13px;
  color: #e2e8f0;
}

.side-panel__section-count {
  font-size: 11px;
  color: #94a3b8;
  margin-left: auto;
}

.cast-panel__list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cast-card {
  padding: 8px;
  border-radius: 8px;
  background: #1e293b;
  border: 1px solid #334155;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.cast-card:active {
  cursor: grabbing;
}

.cast-panel__group {
  margin-bottom: 12px;
}

.cast-panel__group-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
  padding: 2px 8px;
  border-radius: 4px;
}

.cast-panel__group-label--assigned {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.12);
}

.cast-panel__group-label--free {
  color: #6ee7b7;
  background: rgba(16, 185, 129, 0.12);
}

.cast-panel__group-label--status {
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.12);
}

.cast-card--assigned {
  background: rgba(139, 92, 246, 0.08);
}

.cast-card--status {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.35);
}

.cast-card__status-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cast-card__status--tappable {
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-decoration: underline dotted rgba(148, 163, 184, 0.6);
  text-underline-offset: 3px;
}

.cast-card__status {
  font-size: 11px;
  color: #f87171;
  font-weight: 700;
}

.cast-card--dragging {
  opacity: 0.4;
}

.cast-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cast-card__name {
  font-weight: 700;
  font-size: 13px;
  color: #f1f5f9;
}

.cast-card__shift-end {
  font-size: 10px;
  color: #64748b;
}

.cast-card__assignment {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}

.cast-card__table-badge {
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  background: rgba(139, 92, 246, 0.5);
  padding: 1px 8px;
  border-radius: 4px;
}

.cast-card__duration {
  font-size: 10px;
  color: #94a3b8;
  font-weight: 400;
}

.cast-card__free {
  font-size: 11px;
  color: #10b981;
  font-weight: 600;
}

.cast-card--upcoming {
  border-color: #334155;
  border-style: dashed;
  opacity: 0.7;
}

.cast-card--staff {
  cursor: default;
  background: #1a2233;
}

.cast-card__role {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
  text-transform: capitalize;
}

.cast-card__schedule {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
}

/* 本日の予約 */
.reservation-panel__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.reservation-panel__item {
  font-size: 13.5px;
  padding: 6px 8px;
  border: 1px solid rgba(34, 211, 238, 0.2);
  border-radius: 6px;
  cursor: pointer;
}

/* 行タップで詳細が開くことを示すシェブロン */
.reservation-panel__caret {
  margin-left: 2px;
  transition: transform 0.15s;
}

.reservation-panel__caret--open {
  transform: rotate(180deg);
}

/* コース未選択などで卓作成できない予約の警告 */
.reservation-panel__warn {
  font-size: 12.5px;
  font-weight: 700;
  color: #b45309;
  background: rgba(232, 162, 61, 0.12);
  border: 1px solid rgba(232, 162, 61, 0.5);
  border-radius: 8px;
  padding: 0 7px;
  white-space: nowrap;
}

/* 行タップで開く予約内容 */
.reservation-panel__detail {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed rgba(148, 163, 184, 0.3);
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.reservation-panel__detail-row {
  display: flex;
  gap: 8px;
  font-size: 13px;
  line-height: 1.5;
}

.reservation-panel__detail-key {
  flex: 0 0 40px;
  color: #94a3b8;
}

.reservation-panel__detail-hint {
  font-size: 12.5px;
  color: #b45309;
  margin-top: 4px;
  line-height: 1.5;
}

/* （終了未設定）などの補足。途中で折り返さずまとまりで改行する */
.reservation-panel__detail-note {
  color: #94a3b8;
  white-space: nowrap;
  display: block;
}

/* この予約から卓作成済み（来店中）: 緑系で卓との関連を示す */
.reservation-panel__item--linked {
  border-color: rgba(74, 222, 128, 0.45);
  background: rgba(74, 222, 128, 0.06);
}

.reservation-panel__linked {
  font-size: 12.5px;
  font-weight: 700;
  color: #4ade80;
  white-space: nowrap;
}

.reservation-panel__main {
  display: flex;
  align-items: center;
  gap: 4px 8px;
  /* チップ類が収まらない幅では折り返す（nowrapだとパネル外にはみ出す） */
  flex-wrap: wrap;
}

.reservation-panel__time,
.reservation-panel__count,
.reservation-panel__linked {
  white-space: nowrap;
}

.reservation-panel__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  white-space: nowrap;
}

.reservation-panel__time {
  font-weight: 700;
  color: #22d3ee;
}

.reservation-panel__count {
  font-weight: 600;
}

.reservation-panel__table {
  color: #94a3b8;
}

.reservation-panel__label {
  color: #94a3b8;
  max-width: 150px;
}

/* 未出勤アラート（出勤予定時刻超過） */
.cast-card--late {
  border-color: rgba(239, 68, 68, 0.6);
  background: rgba(239, 68, 68, 0.08);
}

.cast-card__late-badge {
  margin-left: auto;
  font-size: 12px;
  font-weight: 700;
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 4px;
  padding: 0 4px;
  white-space: nowrap;
}

/* フロアからの勤怠操作 */
.cast-card__actions {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.cast-card__status-btn {
  margin-left: auto;
  opacity: 0.6;
}

.cast-card__status-btn:hover {
  opacity: 1;
}

/* テーブル残り時間 */
.table-time__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table-time__item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 6px;
  border-radius: 6px;
  background: #0f172a;
  font-size: 12px;
  gap: 4px;
}

.table-time__left {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.table-time__casts {
  font-size: 10px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table-time__item--warn {
  background: rgba(239, 68, 68, 0.15);
}

.table-time__name {
  color: #94a3b8;
  font-weight: 600;
}

.table-time__remaining {
  color: #6ee7b7;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
}

.table-time__item--warn .table-time__remaining {
  color: #ef4444;
}

/* フロアレイアウト未設定時のエンプティステート */
.floor-empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 200px);
  padding: 32px 16px;
}

.floor-empty-state__inner {
  max-width: 520px;
  text-align: center;
  padding: 40px 32px;
  border-radius: 12px;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.floor-empty-state__icon {
  color: #94a3b8 !important;
  margin-bottom: 16px;
}

.floor-empty-state__title {
  font-weight: 700 !important;
  color: #fff;
  margin-bottom: 12px;
}

.floor-empty-state__description {
  font-size: 14px;
  line-height: 1.7;
  color: #cbd5e1;
  margin-bottom: 20px;
}

.floor-empty-state__steps {
  text-align: left;
  display: inline-block;
  margin: 0 auto 24px;
  padding-left: 20px;
  font-size: 14px;
  line-height: 1.9;
  color: #cbd5e1;
}

/* ライトモード調整 */
.theme--light .floor-empty-state__inner {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.theme--light .floor-empty-state__icon {
  color: rgba(0, 0, 0, 0.4) !important;
}

.theme--light .floor-empty-state__title {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-empty-state__description,
.theme--light .floor-empty-state__steps {
  color: rgba(0, 0, 0, 0.7);
}

/* ========================================================================
 * ライトモードオーバーライド
 * Vuetify2は v-application に .theme--light / .theme--dark を付けるため、
 * 祖先セレクタで現在のテーマを判定できる。Halls.vue と同じ明色調に揃える。
 * ====================================================================== */

/* マップキャンバス・ゾーン */
.theme--light .floor-canvas-wrapper {
  background: #fafafa;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .floor-canvas {
  background-image:
    radial-gradient(circle, rgba(0, 0, 0, 0.08) 1px, transparent 1px);
}

.theme--light .floor-hall-label {
  color: rgba(0, 0, 0, 0.6);
  background: rgba(255, 255, 255, 0.85);
  border-bottom-color: rgba(0, 0, 0, 0.08);
}

.theme--light .floor-zone__label {
  color: rgba(0, 0, 0, 0.6);
}

/* テーブルカード */
.theme--light .floor-table--occupied,
.theme--light .floor-table--designated {
  background: rgba(255, 255, 255, 0.95);
}

/* 終了アラート状態（黄=ending / 赤=expired）は、ライトモードでは白背景に細い枠線だけだと
   埋もれて視認できない。背景にも淡い色を敷き、枠線を濃くしてひと目で分かるようにする
   （旧 TableInUse.vue の「淡い背景 + 枠」方針に揃える）。
   通常カード(.floor-table)とコンパクトカード(.compact-card)の両方に状態クラスが付くため、
   コンパクト側は `.compact-card.floor-table--xxx` で詳細度を上げ、灰色枠ルールに勝たせる。 */
.theme--light .floor-table--ending,
.theme--light .compact-card.floor-table--ending {
  background: rgba(245, 158, 11, 0.16);
  border-color: rgba(217, 119, 6, 1);
}

.theme--light .floor-table--expired,
.theme--light .compact-card.floor-table--expired {
  background: rgba(239, 68, 68, 0.16);
  border-color: rgba(220, 38, 38, 1);
}

.theme--light .floor-table--empty {
  background: rgba(255, 255, 255, 0.7);
  border-color: rgba(0, 0, 0, 0.2);
}

.theme--light .floor-table:has(.floor-table__hold) {
  background: #f5f5f5;
  border-color: rgba(0, 0, 0, 0.15);
}

.theme--light .floor-table:hover {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.08);
}

.theme--light .floor-table__name {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-table__guest-badge {
  background: rgba(0, 0, 0, 0.06);
  color: rgba(0, 0, 0, 0.7);
}

.theme--light .floor-table__time {
  color: #059669;
}

/* 状態色は `.theme--light .floor-table__time`（詳細度2）に負けないよう同じ詳細度で上書きする */
.theme--light .floor-table__time--ending {
  color: #b45309;
}

.theme--light .floor-table__time--expired {
  color: #dc2626;
}

.theme--light .floor-table__sub,
.theme--light .floor-table__total,
.theme--light .floor-table__empty-name,
.theme--light .floor-table__start {
  color: rgba(0, 0, 0, 0.6);
}

.theme--light .floor-table__cast-chip {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-table__cast-overflow {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .floor-table__cast-overflow:hover {
  background: rgba(0, 0, 0, 0.12);
}

.theme--light .floor-table__cast-popover {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* コンパクトビュー */
.theme--light .compact-card {
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .compact-card__name {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .compact-card__time {
  color: #059669;
}

.theme--light .compact-card__time--ending {
  color: #b45309;
}

.theme--light .compact-card__time--expired {
  color: #dc2626;
}

.theme--light .compact-card__range,
.theme--light .compact-card__count,
.theme--light .compact-card__start {
  color: rgba(0, 0, 0, 0.6);
}

.theme--light .compact-card__total {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .compact-card__cast {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .compact-card__casts:hover,
.theme--light .compact-card__casts:focus-visible {
  background: rgba(0, 0, 0, 0.04);
}

.theme--light .compact-card__cast-overflow {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.87);
}

/* サイドパネル */
/* ── サイドパネルのタブ / フィルタ / 待機2列グリッド（2026-08-03 タブ化） ── */
.side-tabs {
  position: sticky;
  top: 0;
  z-index: 6;
  /* 各タブはラベル幅+同じ左右余白で、タブ同士を等間隔に配置する
     （等幅割りだと長い「キャスト」だけ端に張り付いて不揃いに見えるため） */
  display: flex;
  justify-content: space-evenly;
  background: #1a1a2e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.side-tab {
  flex: 0 1 auto;
  border: none;
  background: transparent;
  color: #94a3b8;
  padding: 9px 6px 7px;
  font-size: 13.5px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border-bottom: 2px solid transparent;
  font-family: inherit;
  /* 幅200pxのサイドバーでラベルが2行に折れないようにする */
  white-space: nowrap;
  min-width: 0;
}

.side-tab--on {
  color: #a48afd;
  border-bottom-color: #a48afd;
}

.side-tab__count {
  font-size: 11.5px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 0 4px;
  color: #e2e8f0;
}

/* 未出勤あり: 幅を取らない赤ドットで警告する */
.side-tab__alert-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ef4444;
  flex: 0 0 auto;
}

.cast-filter-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cast-fchip {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: #94a3b8;
  border-radius: 13px;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}

.cast-fchip--on {
  background: #9155fd;
  border-color: #9155fd;
  color: #fff;
}

.cast-fchip__n {
  opacity: 0.8;
  font-weight: 400;
}

.free-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.free-chip {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 8px 6px 8px 9px;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  min-width: 0;
  min-height: 38px;
}

.free-chip__name {
  font-size: 14px;
  font-weight: 600;
  color: #e2e8f0;
  min-width: 0;
}

.free-chip__status-btn {
  margin-left: auto;
  flex: 0 0 auto;
}

.side-panel__empty {
  font-size: 13.5px;
  color: #94a3b8;
  text-align: center;
  padding: 20px 10px;
}

.theme--light .side-tabs {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .free-chip {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .free-chip__name {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .cast-fchip {
  border-color: rgba(0, 0, 0, 0.25);
  color: rgba(0, 0, 0, 0.55);
}

.theme--light .cast-fchip--on {
  background: #9155fd;
  border-color: #9155fd;
  color: #fff;
}

.theme--light .side-panel__section {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .side-panel__section-title {
  color: rgba(0, 0, 0, 0.87);
}

/* 出勤キャストカード */
.theme--light .cast-card {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.12);
}

.theme--light .cast-card--assigned {
  background: rgba(139, 92, 246, 0.06);
}

.theme--light .cast-card__name {
  color: rgba(0, 0, 0, 0.87);
}

/* 出勤キャストセクション補助テキスト */
.theme--light .side-panel__section-count {
  color: rgba(0, 0, 0, 0.6);
}

.theme--light .cast-panel__group-label--assigned {
  color: #6d28d9;
  background: rgba(139, 92, 246, 0.16);
}

.theme--light .cast-panel__group-label--free {
  color: #047857;
  background: rgba(16, 185, 129, 0.16);
}

.theme--light .cast-panel__group-label--status {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.14);
}

.theme--light .cast-card--status {
  background: rgba(239, 68, 68, 0.06);
  border-color: rgba(239, 68, 68, 0.3);
}

.theme--light .cast-card__status-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cast-card__status--tappable {
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
  text-decoration: underline dotted rgba(148, 163, 184, 0.6);
  text-underline-offset: 3px;
}

.cast-card__status {
  color: #b91c1c;
}

.theme--light .cast-card__shift-end,
.theme--light .cast-card__duration {
  color: rgba(0, 0, 0, 0.54);
}

.theme--light .cast-card__free {
  color: #047857;
}

.theme--light .cast-card__schedule {
  color: rgba(0, 0, 0, 0.6);
}

/* テーブル残り時間 */
.theme--light .table-time__item {
  background: #f5f5f5;
}

.theme--light .table-time__name {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .table-time__casts {
  color: rgba(0, 0, 0, 0.54);
}

.theme--light .table-time__remaining {
  color: #059669;
}

/* クリックで解除ダイアログを開くキャストチップ/バッジ */
.floor-table__cast-chip--clickable {
  cursor: pointer;
  transition: background-color 0.15s ease, filter 0.15s ease;
}

.floor-table__cast-chip--clickable:hover {
  background-color: rgba(239, 68, 68, 0.12);
  filter: brightness(1.05);
}

.cast-card__table-badge--clickable {
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.1s ease;
}

.cast-card__table-badge--clickable:hover {
  filter: brightness(1.15);
  transform: translateY(-1px);
}

/* キャスト解除ダイアログのリスト */
.remove-dialog__item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-left: 3px solid #64748b;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease;
}

.remove-dialog__item:hover {
  background-color: rgba(148, 163, 184, 0.08);
}

.remove-dialog__item-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.remove-dialog__item-name {
  font-size: 14px;
  font-weight: 500;
}
</style>

<!-- ワイドモード用のグローバル CSS。<html> に floor-wide-mode クラスが付いた時に
     Vuetify のサイドバー/AppBar を隠し、main の余白をリセットする。 -->
<style>
html.floor-wide-mode .v-navigation-drawer.app-navigation-menu,
html.floor-wide-mode .v-app-bar.app-bar-static {
  display: none !important;
}
html.floor-wide-mode .v-main {
  padding-left: 0 !important;
  padding-top: 0 !important;
}
</style>
